import React, { useState, useEffect, useRef } from "react";
import api, { refreshAccessToken } from "./services/api";
import { AUTORISATIONS_LECTEUR, urlLecteurYoutube } from "./services/lecteurYoutube";
import StudentDashboard from "./components/StudentDashboard";
import AdminPanel from "./components/AdminPanel";
import HelpTour from "./components/HelpTour";
import TeacherApp from "./teacher/TeacherApp";
import TeacherSupportProfile from "./teacher/TeacherSupportProfile";
import { openCinetPayExternally } from "./teacher/services/externalCheckout";
import { isAndroidApp, isAndroidDirect, isAndroidPlayConsumptionOnly, isWindowsApp } from "./platform";
import ParentTeacherPortal from "./components/ParentTeacherPortal";
import StudentReferralPortal from "./components/StudentReferralPortal";
import HelpCenter from "./components/HelpCenter";
import ContactForm from "./components/community/ContactForm";
import DiaporamaAccueil from "./components/DiaporamaAccueil";
import AIAssistantComingSoon from "./components/AIAssistantComingSoon";
import { shouldRenderReferralPortal } from "./utils/referralPortal";
import { shouldRenderSupportPortal } from "./utils/supportPortal";
import { qlAlert } from "./utils/dialogs";
import {
  MESSAGES_VERROU, erreurReseau, finAccesHorsConnexion, getAvecCache, memoriserProfil, profilHorsConnexion,
  purgerHorsConnexion, verrouHorsConnexion,
} from "./services/horsConnexion";

const WINDOWS_TEACHER_SESSION_KEY = "ql_windows_teacher_session";
const teacherBaseUrl = process.env.REACT_APP_TEACHER_URL || "/epreuves/";
const asArray = (data) => {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.results) ? data.results : [];
};
const teacherTokenLoginUrl = (token) => {
  const base = isWindowsApp ? teacherBaseUrl : "/epreuves/";
  return `${base.replace(/\/?$/, "/")}token-login/?token=${encodeURIComponent(token)}`;
};

const openAndroidTeacherSite = async () => {
  const browser = window.Capacitor?.Plugins?.Browser;
  if (typeof browser?.open !== "function") {
    throw new Error("Le navigateur système Android n'est pas disponible.");
  }
  await browser.open({ url: teacherBaseUrl });
};

/* ── Vidéo de démonstration (page d'accueil) ──
   Le lien est géré depuis l'admin Django (VIDEO DEMO) — voir
   loadVideoDemo() plus bas, qui va chercher /api/video-demo/. Tant qu'aucune
   vidéo n'est active, la section affiche un emplacement "bientôt disponible". */
function getYouTubeId(url) {
  if (!url) return null;
  let m = url.match(/[?&]v=([^?&\s]+)/);
  if (m) return m[1];
  m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts|embed|v)\/)([^?&\s]+)/);
  return m ? m[1] : null;
}


/* ── Vitrine "la plateforme en action" (captures d'écran) ──
   11 captures de l'application, réparties par groupes de 4 (un écran, un
   ordinateur portable, une tablette, un téléphone) qui tournent ensemble
   toutes les 4 secondes dans DeviceCluster — comme une photo produit avec
   plusieurs appareils affichant le même contenu à la fois. */
const PLATFORM_SHOWCASE = [
  { src: "/static/showcase/ordi1.PNG", title: "Suivez votre progression",
    text: "Un tableau de bord clair avec votre avancement, vos scores et les prochaines sessions en direct." },
  { src: "/static/showcase/ordi2.PNG", title: "Toutes vos leçons, organisées",
    text: "Chaque leçon est classée par module, prête à être étudiée à votre rythme." },
  { src: "/static/showcase/ordi3.PNG", title: "Testez-vous avec des QCM",
    text: "Des quiz interactifs pour vérifier vos acquis et progresser étape par étape." },
  { src: "/static/showcase/ordi4.PNG", title: "Des exercices corrigés",
    text: "Entraînez-vous sur des exercices variés, avec correction détaillée à l'appui." },
  { src: "/static/showcase/ordi5.PNG", title: "Une banque d'épreuves réelles",
    text: "Accédez à des épreuves déjà tombées, téléchargeables en PDF pour réviser sereinement." },
  { src: "/static/showcase/ordi6.PNG", title: "Rendez vos devoirs en ligne",
    text: "Un clavier mathématique intégré vous permet de saisir formules et équations facilement." },
  { src: "/static/showcase/ordi7.PNG", title: "Suivez tous vos résultats",
    text: "QCM, exercices, devoirs, évaluations : retrouvez toutes vos performances au même endroit." },
  { src: "/static/showcase/ordi8.PNG", title: "Un professeur à portée de message",
    text: "Faites-vous assister par vos enseignants dès que vous rencontrez une difficulté." },
  { src: "/static/showcase/ordi9.PNG", title: "Échangez en groupe",
    text: "Rejoignez les groupes de votre classe pour apprendre et progresser ensemble." },
  { src: "/static/showcase/ordi10.PNG", title: "Revivez les sessions en direct",
    text: "Les cours en direct restent disponibles en replay, à tout moment." },
  { src: "/static/showcase/ordi11.PNG", title: "Un générateur d'épreuves pour les enseignants",
    text: "Les enseignants créent leurs épreuves personnalisées en quelques clics, avec correspondance en temps réel." },
];

/* Composition à 4 appareils (écran, ordinateur portable, tablette,
   téléphone) affichant la plateforme, disposés comme sur une photo produit
   (écran en fond, portable/tablette/téléphone au premier plan). Toutes les
   4,5s, les 4 appareils changent de capture ensemble ("ça circule"). Les
   cadres portrait (tablette/téléphone) recadrent le centre de la capture
   (object-fit: cover) : les 11 captures sont des écrans d'ordinateur
   (paysage), donc pas de version verticale exacte de chaque écran — un
   recadrage plausible plutôt qu'un rendu mobile réel de l'application. */
export function DeviceCluster() {
  const [idx, setIdx] = useState(0);
  const n = PLATFORM_SHOWCASE.length;
  const pick = (offset) => PLATFORM_SHOWCASE[(idx + offset) % n];

  useEffect(() => {
    const t = setInterval(() => setIdx(v => (v + 4) % n), 10000);
    return () => clearInterval(t);
  }, [n]);

  const monitor = pick(0), laptop = pick(1), tablet = pick(2), phone = pick(3);

  return (
    <div className="ql-cluster">
      <div className="ql-cluster-stage">
        <div className="ql-cluster-monitor">
          <div className="ql-cluster-screen">
            <img src={monitor.src} alt={monitor.title} />
          </div>
          <div className="ql-cluster-monitor-stand" />
        </div>
        <div className="ql-cluster-tablet">
          <div className="ql-cluster-tablet-cam" />
          <div className="ql-cluster-screen"><img src={tablet.src} alt={tablet.title} /></div>
        </div>
        <div className="ql-cluster-phone">
          <div className="ql-cluster-phone-notch" />
          <div className="ql-cluster-screen"><img src={phone.src} alt={phone.title} /></div>
        </div>
        <div className="ql-cluster-laptop">
          <div className="ql-cluster-laptop-bar"><span /><span /><span /></div>
          <div className="ql-cluster-screen"><img src={laptop.src} alt={laptop.title} /></div>
          <div className="ql-cluster-laptop-base" />
        </div>
      </div>
      <div className="ql-cluster-caption">
        <h4>{monitor.title}</h4>
        <p>{monitor.text}</p>
      </div>
    </div>
  );
}

/* ── Bouton retour en haut ── */
function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Remonter en haut de la page"
      style={{
        position: "fixed", bottom: 28, right: 24, zIndex: 1000,
        width: 48, height: 48, borderRadius: "50%", border: "none",
        background: "linear-gradient(135deg,#1e3a5f,#2d5a8e)", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.3rem", cursor: "pointer", boxShadow: "0 6px 20px #1e3a5f4d",
        transition: "transform 0.2s, opacity 0.2s",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.background = "#f5820d"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = "linear-gradient(135deg,#1e3a5f,#2d5a8e)"; }}
    >
      <i className="bi bi-arrow-up"></i>
    </button>
  );
}

export function WelcomeBonusModal({ bonus, onClose }) {
  if (!bonus?.granted) return null;
  const endDate = bonus.end_date
    ? new Date(bonus.end_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })
    : null;
  return (
    <div className="ql-welcome-bonus-overlay" role="dialog" aria-modal="true" aria-labelledby="ql-welcome-bonus-title">
      <div className="ql-welcome-confetti" aria-hidden="true">
        {Array.from({ length: 18 }, (_, index) => <span key={index} />)}
      </div>
      <section className="ql-welcome-bonus-card">
        <div className="ql-welcome-bonus-icon" aria-hidden="true"><i className="bi bi-gift-fill"></i></div>
        <span className="ql-welcome-bonus-badge">1 mois offert</span>
        <h2 id="ql-welcome-bonus-title">Bienvenue dans Quick Learning !</h2>
        <p>Votre manuel est activé et nous sommes heureux de vous offrir <strong>30 jours d’abonnement</strong>. Profitez dès maintenant de tous vos contenus, sans paiement{endDate ? ` jusqu’au ${endDate}` : " pendant un mois"}.</p>
        <button type="button" className="btn btn-primary" onClick={onClose}>Commencer à apprendre</button>
      </section>
    </div>
  );
}

/* ── Plans disponibles ── */
const PLANS = [
  { days: 1,   label: "1 jour",   price: 100,   badge: "Essai",       color: "#64748b" },
  { days: 7,   label: "7 jours",  price: 700,   badge: "Semaine",     color: "#3b82f6" },
  { days: 30,  label: "30 jours", price: 3000,  badge: "Mensuel",     color: "#f5820d", popular: true },
  { days: 90,  label: "90 jours", price: 9000,  badge: "Trimestriel", color: "#10b981" },
  { days: 180, label: "6 mois",   price: 15000, badge: "Semestriel",  color: "#6f42c1" },
  { days: 270, label: "9 mois",   price: 20000, badge: "Annuel",      color: "#0d6efd" },
];
const METHODS = [
  { key: "MTN_CM",    label: "MTN Mobile Money",  icon: "bi-phone" },
  { key: "OM_CM",     label: "Orange Money",       icon: "bi-phone" },
];

export function AndroidSubscriptionStatus({ user, onRefresh, onLogout }) {
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState(null);
  const expiration = user?.subscription_end_date
    ? new Date(user.subscription_end_date).toLocaleDateString("fr-FR")
    : null;
  const formula = user?.subscription_plan || user?.subscription_formula || user?.formule_abonnement;
  const refresh = async () => {
    setRefreshing(true);
    setMessage(null);
    try {
      await onRefresh();
      setMessage("Compte actualisé.");
    } catch {
      setMessage("Actualisation impossible. Vérifiez votre connexion puis réessayez.");
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <div className="ql-sub-wall"><div className="ql-sub-wall-card">
      <div className="ql-sub-wall-logo">
        <img src="/static/logo.png" alt="Quick Learning" style={{ height: 48 }} />
        <span className="ql-sub-wall-brand">QUICK LEARNING</span>
      </div>
      <div className="ql-sub-wall-status expired"><i className="bi bi-info-circle"></i><div>
        <strong>Abonnement inactif</strong>
        <p>Votre abonnement ne permet plus d’accéder à ce contenu. Si votre compte a été renouvelé en dehors de cette application, appuyez sur Actualiser mon compte.</p>
      </div></div>
      {(expiration || formula) && <div style={{ margin: "16px 0", color: "#475569" }}>
        {expiration && <div><strong>Expiration :</strong> {expiration}</div>}
        {formula && <div><strong>Formule :</strong> {formula}</div>}
      </div>}
      <p style={{ color: "#475569", margin: "20px 0" }}>
        L’application Android permet de consulter les contenus associés à un compte actif. Aucun achat, renouvellement ou paiement n’est proposé dans l’application.
      </p>
      {message && <div className="alert alert-info" role="status">{message}</div>}
      <button type="button" className="btn btn-primary w-100 ql-account-refresh-btn" disabled={refreshing} onClick={refresh}>
        <i className="bi bi-arrow-clockwise me-2"></i>{refreshing ? "Actualisation…" : "Actualiser mon compte"}
      </button>
      <button type="button" className="btn btn-link w-100 mt-2" onClick={onLogout}>Se déconnecter</button>
    </div></div>
  );
}

export function SubscriptionWall({ user, onSubscribed, onLogout }) {
  const [plan,       setPlan]      = useState(PLANS[2]);
  const [method,     setMethod]    = useState(METHODS[0].key);
  const [phone,      setPhone]     = useState("");
  const [loading,    setLoading]   = useState(false);
  const [polling,    setPolling]   = useState(false);
  const [error,      setError]     = useState(null);
  const [success,    setSuccess]   = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const paymentInitializationLock = useRef(false);
  const [classes,    setClasses]   = useState([]);
  const [classeId,   setClasseId]  = useState(user?.classe_id || "");

  useEffect(() => {
    api.get("classes/").then(r => setClasses(asArray(r.data))).catch(() => setClasses([]));
  }, []);

  const expired = user?.subscription_end_date && !user?.subscription_active;
  const endDate = user?.subscription_end_date
    ? new Date(user.subscription_end_date).toLocaleString("fr-FR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })
    : null;

  const pollStatus = (merchantId, attempts = 0) => {
    if (attempts > 40) {
      setPolling(false);
      setLoading(false);
      setError("Délai dépassé. Vérifiez votre téléphone et réessayez.");
      return;
    }
    setTimeout(async () => {
      try {
        const r = await api.get(`paiements/cinetpay/status/${merchantId}/`);
        if (r.data.status === "success") {
          setPolling(false);
          setSuccess(true);
          setTimeout(() => onSubscribed(), 2000);
        } else if (r.data.status === "failed") {
          setPolling(false);
          setLoading(false);
          setError("Paiement échoué. Vérifiez votre solde et réessayez.");
        } else {
          pollStatus(merchantId, attempts + 1);
        }
      } catch {
        pollStatus(merchantId, attempts + 1);
      }
    }, 4000);
  };

  const handlePay = async () => {
    if (!classeId) { setError("Veuillez sélectionner votre classe avant de continuer."); return; }
    if (!phone.trim()) { setError("Veuillez entrer votre numéro de téléphone."); return; }
    if (paymentInitializationLock.current || polling) return;
    paymentInitializationLock.current = true;
    setError(null);
    setLoading(true);
    try {
      const digits = phone.trim().replace(/\D/g, '').replace(/^237/, '');
      const r = await api.post("paiements/cinetpay/init/", {
        days: plan.days,
        payment_method: method,
        phone_number: `237${digits}`,
        classe_id: classeId,
      });
      if (!r.data.payment_url) throw new Error("URL_CINETPAY_ABSENTE");
      setPaymentUrl(r.data.payment_url);
      setPolling(true);
      pollStatus(r.data.merchant_transaction_id);
      try {
        await openCinetPayExternally(r.data.payment_url);
      } catch (openError) {
        setError("Le paiement est initialisé, mais la page CinetPay n’a pas pu être ouverte. Utilisez le bouton Réouvrir la page CinetPay.");
      }
    } catch (e) {
      console.error("[CinetPay SW] ERREUR API:", e.response?.status, e.response?.data);
      setError(
        e.response?.data?.detail
        || e.response?.data?.message
        || (String(e.message || "").startsWith("URL_CINETPAY") || e.message === "OUVERTURE_EXTERNE_INDISPONIBLE"
          ? "Le paiement a été initialisé, mais la page CinetPay n’a pas pu être ouverte. Utilisez le bouton Réouvrir la page CinetPay."
          : "Erreur lors de l'initiation du paiement.")
      );
      setLoading(false);
      paymentInitializationLock.current = false;
    }
  };

  return (
    <div className="ql-sub-wall">
      <div className="ql-sub-wall-card">
        {/* Logo */}
        <div className="ql-sub-wall-logo">
          <img src="/static/logo.png" alt="Quick Learning" style={{ height: 48 }} />
          <span className="ql-sub-wall-brand">QUICK LEARNING</span>
        </div>

        {/* Message d'état */}
        {expired ? (
          <div className="ql-sub-wall-status expired">
            <i className="bi bi-clock-history"></i>
            <div>
              <strong>Abonnement expiré</strong>
              <p>Votre accès a expiré le {endDate}. Renouvelez pour continuer à apprendre.</p>
            </div>
          </div>
        ) : (
          <div className="ql-sub-wall-status new">
            <i className="bi bi-lock"></i>
            <div>
              <strong>Accès restreint</strong>
              <p>Bonjour {user?.nom_complet || user?.username} ! Souscrivez à un forfait pour accéder à la plateforme.</p>
            </div>
          </div>
        )}

        {success ? (
          <div className="ql-sub-wall-success">
            <i className="bi bi-check-circle-fill"></i>
            <strong>Paiement confirmé !</strong>
            <p>Accès en cours d'activation…</p>
          </div>
        ) : polling ? (
          <div style={{ textAlign: "center", padding: "40px 16px" }}>
            <div className="spinner-border text-primary mb-3" style={{ width: 48, height: 48 }}></div>
            <h6 className="fw-semibold mb-1">Paiement en cours…</h6>
            {error && <div className="alert alert-warning py-2 mt-2" role="alert">{error}</div>}
            <p className="text-muted" style={{ fontSize: "0.9rem" }}>
              Effectuez votre paiement de <strong>{plan.price.toLocaleString("fr-FR")} FCFA</strong> sur la page CinetPay.<br />
              <small>Cette page se mettra à jour automatiquement après confirmation.</small>
            </p>
            {paymentUrl && (
              <button type="button" onClick={() => openCinetPayExternally(paymentUrl).catch(() => setError("Impossible d’ouvrir la page CinetPay."))} className="btn btn-sm btn-outline-primary mt-2">
                <i className="bi bi-box-arrow-up-right me-1"></i>
                Réouvrir la page CinetPay
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Choix du plan */}
            <h3 className="ql-sub-wall-section-title">Choisissez votre forfait</h3>
            <div className="ql-sub-plans">
              {PLANS.map(p => (
                <div
                  key={p.days}
                  className={`ql-sub-plan${plan.days === p.days ? " selected" : ""}${p.popular ? " popular" : ""}`}
                  onClick={() => setPlan(p)}
                >
                  {p.popular && <span className="ql-sub-plan-popular">Populaire</span>}
                  <div className="ql-sub-plan-badge" style={{ background: p.color + "20", color: p.color }}>{p.badge}</div>
                  <div className="ql-sub-plan-label">{p.label}</div>
                  <div className="ql-sub-plan-price">{p.price.toLocaleString("fr-FR")} <span>FCFA</span></div>
                  <div className="ql-sub-plan-unit">{Math.round(p.price / p.days)} FCFA/jour</div>
                </div>
              ))}
            </div>

            {/* Sélection de la classe */}
            <h3 className="ql-sub-wall-section-title">Votre classe</h3>
            <div style={{ marginBottom: "16px" }}>
              <select
                value={classeId}
                onChange={e => setClasseId(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", borderRadius: "8px",
                  border: classeId ? "1px solid #e2e8f0" : "1px solid #f97316",
                  fontSize: "0.9rem", color: classeId ? "#1e293b" : "#94a3b8",
                  background: "#f8fafc", outline: "none", cursor: "pointer",
                }}
              >
                <option value="">— Sélectionnez votre classe —</option>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {!classeId && (
                <p style={{ fontSize: "0.78rem", color: "#f97316", marginTop: "4px", marginBottom: 0 }}>
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Obligatoire — votre classe détermine les manuels accessibles.
                </p>
              )}
            </div>

            {/* Méthode de paiement */}
            <h3 className="ql-sub-wall-section-title">Méthode de paiement</h3>
            <div className="ql-sub-methods">
              {METHODS.map(m => (
                <label key={m.key} className={`ql-sub-method${method === m.key ? " selected" : ""}`}>
                  <input type="radio" name="method" value={m.key} checked={method === m.key} onChange={() => setMethod(m.key)} />
                  <i className={`bi ${m.icon}`}></i>
                  <span>{m.label}</span>
                </label>
              ))}
            </div>

            {/* Numéro de téléphone */}
            <h3 className="ql-sub-wall-section-title">Numéro de téléphone</h3>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", background: "#f8fafc" }}>
                <span style={{ padding: "10px 12px", background: "#e2e8f0", fontWeight: 600, color: "#475569", fontSize: "0.95rem", flexShrink: 0, userSelect: "none" }}>+237</span>
                <input
                  type="tel"
                  placeholder="6XX XX XX XX"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  maxLength={9}
                  style={{ flex: 1, padding: "10px 12px", border: "none", fontSize: "0.95rem", background: "transparent", outline: "none" }}
                />
              </div>
              <p style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "4px", marginBottom: 0 }}>
                Entrez votre numéro sans le préfixe +237
              </p>
            </div>

            {error && <div className="alert alert-danger py-2 mt-2">{error}</div>}

            {/* Récap + bouton */}
            <div className="ql-sub-wall-recap">
              <span>Total à payer</span>
              <strong>{plan.price.toLocaleString("fr-FR")} FCFA</strong>
            </div>
            <button className="btn btn-orange w-100 py-2" disabled={loading} onClick={handlePay}>
              {loading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-lock-fill me-2"></i>}
              Payer {plan.price.toLocaleString("fr-FR")} FCFA — {plan.label}
            </button>
          </>
        )}

        <button className="ql-sub-wall-logout" onClick={onLogout}>
          <i className="bi bi-box-arrow-right me-1"></i>Se déconnecter
        </button>
      </div>

    </div>
  );
}

const FEATURES = [
  {
    icon: "bi-play-circle-fill",
    color: "#3b82f6",
    title: "Cours vidéo clairs et détaillés",
    desc: "Chaque leçon est expliquée en vidéo par des enseignants expérimentés. Les concepts les plus difficiles — démonstrations de Maths, expériences de Physique, réactions de Chimie — sont illustrés étape par étape pour une compréhension durable.",
    tag: "Vidéos HD",
  },
  {
    icon: "bi-clipboard-check-fill",
    color: "#f5820d",
    title: "Exercices corrigés pas à pas",
    desc: "Des centaines d'exercices tirés des programmes officiels, avec des corrections détaillées. Chaque étape est expliquée pour que tu comprennes non seulement la réponse, mais aussi le raisonnement — exactement ce que demandent les correcteurs au BEPC, au Probatoire et au Baccalauréat.",
    tag: "+500 exercices",
  },
  {
    icon: "bi-patch-check-fill",
    color: "#22c55e",
    title: "QCM interactifs avec corrigés",
    desc: "Teste tes connaissances avec des QCM chronométrés sur chaque chapitre. Après chaque réponse, un corrigé immédiat t'explique pourquoi tu as juste ou faux — un entraînement idéal pour les épreuves à choix multiples des examens officiels.",
    tag: "Auto-évaluation",
  },
  {
    icon: "bi-bar-chart-line-fill",
    color: "#8b5cf6",
    title: "Suivi de progression personnalisé",
    desc: "Ton tableau de bord affiche ta progression chapitre par chapitre : leçons vues, exercices réussis, scores aux QCM, temps passé. Tu identifies tes lacunes et tu sais exactement où concentrer tes efforts pour progresser efficacement.",
    tag: "Tableau de bord",
  },
  {
    icon: "bi-chat-dots-fill",
    color: "#0ea5e9",
    title: "Messagerie avec les enseignants",
    desc: "Tu bloques sur un exercice ou une notion ? Envoie un message directement à ton enseignant depuis la plateforme. Tu reçois une réponse claire et rapide sans avoir à attendre la prochaine heure de cours — un vrai soutien scolaire à portée de main.",
    tag: "Support direct",
  },
  {
    icon: "bi-book-fill",
    color: "#ec4899",
    title: "Manuel numérique enrichi",
    desc: "Retrouve l'intégralité de ton manuel en version numérique, organisée en modules et leçons. Le contenu physique que tu connais déjà est enrichi de vidéos, exercices et ressources complémentaires — rendant ton manuel bien plus puissant qu'en version papier.",
    tag: "Cours complets",
  },
  {
    icon: "bi-mortarboard-fill",
    color: "#f59e0b",
    title: "Préparation ciblée aux examens",
    desc: "Des évaluations types BEPC, Probatoire et Baccalauréat, des sujets des années précédentes et des révisions guidées par matière. La plateforme t'aide à t'entraîner dans les conditions réelles de chaque examen pour arriver confiant et préparé le jour J.",
    tag: "BEPC · Probatoire · BAC",
  },
  {
    icon: "bi-display-fill",
    color: "#14b8a6",
    title: "Accessible partout, à tout moment",
    desc: "Quick Learning fonctionne sur téléphone, tablette et ordinateur. Que tu sois chez toi, au collège ou en déplacement, tu accèdes à tous tes cours et exercices 24h/24, 7j/7 — sans dépendre d'un horaire ou d'un lieu fixe.",
    tag: "24h/24 · 7j/7",
  },
  {
    icon: "bi-magic",
    color: "#6366f1",
    title: "Génération automatique d'épreuves",
    desc: "Les enseignants créent des épreuves personnalisées en quelques clics : choix des modules et des leçons, niveau de difficulté, mise en page — le sujet et son corrigé sont générés automatiquement, prêts à imprimer ou à partager.",
    tag: "Pour les enseignants",
  },
];

const STATS = [
  { icon: "bi-pencil-square", number: "+500", label: "Exercices résolus de différents modules" },
  { icon: "bi-book-half",     number: "+750", label: "Leçons bien expliquées" },
  { icon: "bi-play-btn",      number: "+10h", label: "De contenu vidéo disponible" },
  { icon: "bi-wallet2",       number: "100 F", label: "Souscrivez à partir de 100 F/jour" },
];

// Aperçu public (sans connexion) de quelques professeurs de soutien scolaire déjà
// approuvés, affiché sur la page d'accueil au-dessus du bouton "Cliquez ici".
function SoutienTeacherPreview() {
  const [teachers, setTeachers] = useState(null);
  useEffect(() => {
    api.get("parent-teacher/directory/", { params: { page_size: 12 } })
      .then(r => {
        const ranked = asArray(r.data)
          .slice()
          .sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0));
        setTeachers(ranked.slice(0, 4));
      })
      .catch(() => setTeachers([]));
  }, []);
  if (!teachers || teachers.length === 0) return null;
  return (
    <div className="ql-soutien-preview">
      {teachers.map(teacher => (
        <div key={teacher.id} className="ql-soutien-preview-card">
          {teacher.photo_url
            ? <img src={teacher.photo_url} alt="" />
            : <div className="ql-soutien-preview-avatar"><i className="bi bi-person"></i></div>}
          <strong>{teacher.full_name}</strong>
          <span>{(teacher.subjects || []).slice(0, 2).map(s => s.name).join(", ")}</span>
          {teacher.rating?.average != null && (
            <span className="ql-soutien-preview-rating"><i className="bi bi-star-fill"></i> {teacher.rating.average}/5</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Formate automatiquement un code manuel : majuscules, espaces→tirets, pas de double tiret
// Codes du personnel (comptable, Community Manager) : la partie aléatoire peut
// contenir « ENS » sans qu'il s'agisse d'un code enseignant.
const estCodeComptable = (code) => code.toUpperCase().includes("COMP");
const estCodeCommunity = (code) => code.toUpperCase().startsWith("QL-COMM-");
const estCodeSecretariat = (code) => code.toUpperCase().startsWith("QL-SEC-");
const estCodeAmbassadeur = (code) => code.toUpperCase().startsWith("QL-AMB-");
const estCodePersonnel = (code) => estCodeComptable(code) || estCodeCommunity(code) || estCodeSecretariat(code) || estCodeAmbassadeur(code);

function formatCode(raw) {
  return raw
    .toUpperCase()
    .replace(/ /g, "-")
    .replace(/[^A-Z0-9-]/g, "")
    .replace(/-{2,}/g, "-");
}

function App() {
  const [user, setUser] = useState(null);
  // Parent connecté au soutien scolaire (connu tout de suite pour éviter un affichage intermédiaire).
  const [parentConnecte, setParentConnecte] = useState(() => {
    try { return !!localStorage.getItem("ql_parent_user") && !!localStorage.getItem("access_token"); } catch { return false; }
  });
  const [showTour, setShowTour] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(isWindowsApp || isAndroidApp);
  const [windowsSection, setWindowsSection] = useState("accueil");
  const [androidSection, setAndroidSection] = useState("accueil");
  const [androidMenuOpen, setAndroidMenuOpen] = useState(false);
  const [androidTeacherRestricted, setAndroidTeacherRestricted] = useState(false);
  const [androidTeacherSiteError, setAndroidTeacherSiteError] = useState(null);
  const [welcomeBonus, setWelcomeBonus] = useState(null);

  const openTour  = () => setShowTour(true);
  const closeTour = () => {
    localStorage.setItem("ql_tour_seen", "1");
    setShowTour(false);
  };

  // Auth form fields
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginCodeManuel, setLoginCodeManuel] = useState("");
  const registrationPending = useRef(false);
  const [registrationStage, setRegistrationStage] = useState("");
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showPasswordSuggestions, setShowPasswordSuggestions] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken"
  const [usernameSuggestions, setUsernameSuggestions] = useState([]);

  // Champs profil élève / enseignant
  const [registerNomComplet, setRegisterNomComplet] = useState("");
  const [registerEcole, setRegisterEcole] = useState("");
  const [registerClasseId, setRegisterClasseId] = useState("");
  const [registerPhoto, setRegisterPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [registerCodeManuel, setRegisterCodeManuel] = useState("");
  const [registerReferralCode, setRegisterReferralCode] = useState("");
  const [registerEstEnseignant, setRegisterEstEnseignant] = useState(false);
  const [publicClasses, setPublicClasses] = useState([]);

  // Dashboard data
  const [error, setError] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [manuals, setManuals] = useState([]);
  const [videoDemos, setVideoDemos] = useState([]);
  const [openVideoDemo, setOpenVideoDemo] = useState(null);
  const [showAllManuals, setShowAllManuals] = useState(false);
  const [modules, setModules] = useState([]);

  // Modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState("login");

  // Mot de passe oublié
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState(null);
  // Réinitialisation via lien email
  const [resetUid, setResetUid] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPassword2, setResetPassword2] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const openLogin = () => {
    setAuthTab("login");
    setError(null);
    setShowAuthModal(true);
  };

  const closeModal = () => {
    setShowAuthModal(false);
    setError(null);
  };

  const saveTokens = ({ access, refresh }) => {
    localStorage.setItem("access_token", access);
    localStorage.setItem("refresh_token", refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem(WINDOWS_TEACHER_SESSION_KEY);
  };

  const restrictAndroidTeacher = () => {
    if (!isAndroidApp) return;
    clearTokens();
    setUser(null);
    setAndroidMenuOpen(false);
    setAndroidTeacherSiteError(null);
    setAndroidTeacherRestricted(true);
    setShowAuthModal(false);
  };

  const loadProfile = async ({ openTeacherSpace = true } = {}) => {
    try {
      const r = await api.get("me/", { params: { _: Date.now() } });
      let profile = r.data?.user || r.data;

      // Certaines versions historiques de /me/ ne remontent pas correctement
      // le rôle des comptes générateurs. L'API enseignant reste l'autorité :
      // elle répond 403 aux élèves et ne réalise aucune écriture.
      if (isWindowsApp && !profile?.is_enseignant && !profile?.is_generateur) {
        try {
          await api.get("teacher/dashboard/");
          profile = { ...profile, is_enseignant: true };
          localStorage.setItem(WINDOWS_TEACHER_SESSION_KEY, "1");
        } catch {
          localStorage.removeItem(WINDOWS_TEACHER_SESSION_KEY);
        }
      } else if (isWindowsApp && (profile?.is_enseignant || profile?.is_generateur)) {
        localStorage.setItem(WINDOWS_TEACHER_SESSION_KEY, "1");
      }

      if (isAndroidPlayConsumptionOnly && profile.is_enseignant) {
        restrictAndroidTeacher();
        return null;
      }
      // Le dashboard React Windows utilise directement le JWT. Le Web garde
      // le parcours Django historique et Android reste restreint.
      if (openTeacherSpace && profile.is_enseignant && !window.location.pathname.startsWith("/epreuves") && !window.location.pathname.startsWith("/soutien") && !window.location.pathname.startsWith("/profil-soutien")) {
        const access = localStorage.getItem("access_token");
        if (access && !isWindowsApp) {
          window.location.href = teacherTokenLoginUrl(access);
          return null;
        }
      }
      // Un parent (soutien scolaire) n'a pas d'espace élève : sur l'accueil, il voit le
      // site public avec un accès direct à son espace parent, jamais le mur d'abonnement.
      if (profile?.is_parent) {
        setParentConnecte(true);
        setUser(null);
        return null;
      }
      setParentConnecte(false);
      try { localStorage.removeItem("ql_parent_user"); } catch { /* stockage indisponible */ }
      memoriserProfil(profile, { appliGenerateur: isWindowsApp || isAndroidDirect }).catch(() => {});
      setUser(profile);
      return profile;
    } catch (erreur) {
      return reprendreHorsConnexion(erreur);
    }
  };

  // Démarrage sans réseau : l'élève retrouve son espace avec le profil gardé,
  // tant qu'il s'est connecté au moins une fois dans les dernières 48 h.
  const reprendreHorsConnexion = async (erreur) => {
    if (erreurReseau(erreur)) {
      const profil = await profilHorsConnexion();
      if (profil) {
        setUser(profil);
        return profil;
      }
      const verrou = finAccesHorsConnexion() && verrouHorsConnexion();
      if (verrou) qlAlert(MESSAGES_VERROU[verrou], "warning");
    }
    setUser(null);
    return null;
  };

  const loadDashboard = async () => {
    try {
      const r = await getAvecCache("dashboard/");
      setDashboardSummary(r.data);
    } catch {
      setDashboardSummary(null);
    }
  };

  const loadManuals = async () => {
    try {
      const r = await api.get("manuels/");
      setManuals(asArray(r.data));
    } catch {
      setManuals([]);
    }
  };

  const loadVideoDemo = async () => {
    try {
      const r = await api.get("video-demo/");
      setVideoDemos(asArray(r.data).filter(v => getYouTubeId(v.url)));
    } catch {
      setVideoDemos([]);
    }
  };

  const loadModules = async () => {
    try {
      const r = await getAvecCache("modules/");
      setModules(asArray(r.data));
    } catch {
      setModules([]);
    }
  };

  useEffect(() => {
    loadManuals();
    loadVideoDemo();
    api.get("classes/").then((r) => setPublicClasses(asArray(r.data))).catch(() => setPublicClasses([]));

    if (isWindowsApp) {
      const restoreWindowsSession = async () => {
        try {
          let accessToken = localStorage.getItem("access_token");
          const refreshToken = localStorage.getItem("refresh_token");

          if (!accessToken && refreshToken) {
            accessToken = await refreshAccessToken();
          }

          if (accessToken) {
            const profile = await loadProfile();
            if (profile) {
              await Promise.all([loadDashboard(), loadModules()]);
            }
          }
        } catch (erreur) {
          if (await reprendreHorsConnexion(erreur)) await Promise.all([loadDashboard(), loadModules()]);
        } finally {
          setSessionChecking(false);
        }
      };

      restoreWindowsSession();
    } else if (isAndroidApp) {
      const restoreAndroidSession = async () => {
        try {
          let accessToken = localStorage.getItem("access_token");
          const refreshToken = localStorage.getItem("refresh_token");

          if (!accessToken && refreshToken) {
            accessToken = await refreshAccessToken();
          }

          if (accessToken) {
            const profile = await loadProfile();
            if (profile) {
              await Promise.all([loadDashboard(), loadModules()]);
            }
          }
        } catch (erreur) {
          if (await reprendreHorsConnexion(erreur)) await Promise.all([loadDashboard(), loadModules()]);
        } finally {
          setSessionChecking(false);
        }
      };

      restoreAndroidSession();
    } else {
    const token = localStorage.getItem("access_token");
    if (token) {
      loadProfile();
      loadDashboard();
      loadModules();
    }
    }
    // Détecter lien de réinitialisation de mot de passe
    const params = new URLSearchParams(window.location.search);
    const uid = params.get("reset_uid");
    const rtoken = params.get("reset_token");
    if (uid && rtoken) {
      setResetUid(uid);
      setResetToken(rtoken);
      setAuthTab("reset");
      setShowAuthModal(true);
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isWindowsApp) return undefined;

    const handlePopState = (event) => {
      setWindowsSection(event.state?.quickLearningSection || "accueil");
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.history.replaceState(
      { ...window.history.state, quickLearningSection: "accueil" },
      document.title
    );
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (!isAndroidApp) return undefined;

    const handlePopState = (event) => {
      setAndroidSection(event.state?.quickLearningAndroidSection || "accueil");
      setAndroidMenuOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.history.replaceState(
      { ...window.history.state, quickLearningAndroidSection: "accueil" },
      document.title
    );
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateWindowsSection = (section) => {
    if (!isWindowsApp || section === windowsSection) return;
    setWindowsSection(section);
    window.history.pushState(
      { ...window.history.state, quickLearningSection: section },
      document.title
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navigateAndroidSection = (section) => {
    if (!isAndroidApp) return;
    setAndroidMenuOpen(false);
    if (section !== androidSection) {
      setAndroidSection(section);
      window.history.pushState(
        { ...window.history.state, quickLearningAndroidSection: section },
        document.title
      );
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showLandingSection = (section) => {
    if (isWindowsApp) return windowsSection === section;
    if (isAndroidApp) return androidSection === section;
    return true;
  };

  const sendLocation = () => {
    const envoyerPosition = (lat, lng) => {
      api.post("me/location/", { latitude: lat, longitude: lng }).catch(() => {});
    };

    const fallbackIP = () => {
      fetch("https://ipapi.co/json/")
        .then(r => r.json())
        .then(d => { if (d.latitude && d.longitude) envoyerPosition(d.latitude, d.longitude); })
        .catch(() => {});
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => envoyerPosition(pos.coords.latitude, pos.coords.longitude),
        () => fallbackIP(),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } else {
      fallbackIP();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const r = await api.post("login/", {
        username: loginUsername,
        password: loginPassword,
        code_manuel: loginCodeManuel,
      });
      saveTokens(r.data);
      if (isWindowsApp && r.data.is_enseignant) {
        localStorage.setItem(WINDOWS_TEACHER_SESSION_KEY, "1");
      }
      if (isAndroidPlayConsumptionOnly && r.data.is_enseignant) {
        setLoginPassword("");
        setLoginCodeManuel("");
        restrictAndroidTeacher();
        return;
      }
      // Sous Windows, l'enseignant reste dans React. Le Web conserve le
      // token-login historique qui crée le cookie du fallback Django.
      const teacherSpaceOpened = r.data.redirect_to && r.data.is_enseignant;
      if (teacherSpaceOpened && !isWindowsApp && !isAndroidDirect) {
        window.location.href = teacherTokenLoginUrl(r.data.access);
        return;
      }
      setLoginPassword("");
      setLoginCodeManuel("");
      const profile = await loadProfile({ openTeacherSpace: !teacherSpaceOpened });
      if (isAndroidApp && !profile) return;
      await loadDashboard();
      await loadManuals();
      await loadModules();
      setShowAuthModal(false);
      sendLocation();
      // La visite guidée animée de l'espace se lance d'elle-même au premier accès.
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de se connecter.");
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRegisterPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // ── Mot de passe oublié ───────────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError(null);
    try {
      await api.post("password-reset/", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur d'envoi. Vérifiez votre connexion.";
      setForgotError(msg);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword !== resetPassword2) {
      setResetError("Les mots de passe ne correspondent pas.");
      return;
    }
    setResetLoading(true);
    setResetError(null);
    try {
      await api.post("password-reset/confirm/", {
        uid: resetUid,
        token: resetToken,
        password: resetPassword,
      });
      setResetDone(true);
    } catch (err) {
      setResetError(err.response?.data?.detail || "Lien expiré ou invalide. Refaites une demande.");
    } finally {
      setResetLoading(false);
    }
  };

  // ── Vérification username (debounce 500ms) ────────────────────────────────
  const usernameTimerRef = React.useRef(null);
  const handleUsernameChange = (val) => {
    setRegisterUsername(val);
    setUsernameStatus(null);
    setUsernameSuggestions([]);
    if (usernameTimerRef.current) clearTimeout(usernameTimerRef.current);
    if (val.trim().length < 3) return;
    setUsernameStatus("checking");
    usernameTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`check-username/?username=${encodeURIComponent(val.trim())}`);
        setUsernameStatus(r.data.available ? "available" : "taken");
        setUsernameSuggestions(asArray(r.data.suggestions));
      } catch { setUsernameStatus(null); }
    }, 500);
  };

  // ── Force du mot de passe ──────────────────────────────────────────────────
  const getPasswordStrength = (pwd) => {
    const criteria = {
      length:    pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      number:    /[0-9]/.test(pwd),
      special:   /[^A-Za-z0-9]/.test(pwd),
    };
    const score = Object.values(criteria).filter(Boolean).length;
    if (pwd.length === 0) return { score: 0, label: "", color: "#e2e8f0", criteria };
    if (score <= 1)       return { score: 1, label: "Faible",  color: "#ef4444", criteria };
    if (score === 2)      return { score: 2, label: "Moyen",   color: "#f59e0b", criteria };
    if (score === 3)      return { score: 3, label: "Bon",     color: "#3b82f6", criteria };
    return               { score: 4, label: "Fort",    color: "#22c55e", criteria };
  };

  // ── Suggestions mémorables à partir du nom ────────────────────────────────
  const generatePasswordSuggestions = (nom) => {
    const parts  = (nom || "Eleve Quick").trim().split(/\s+/);
    const first  = parts[0] || "Eleve";
    const last   = parts[1] || "Quick";
    const cap    = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    const rev    = (s) => s.split("").reverse().join("");
    const initials = parts.map(p => p[0].toUpperCase()).join("");
    const yr = new Date().getFullYear();
    const specials = ["@", "#", "!", "$"];
    const rand3 = () => Math.floor(100 + Math.random() * 900);

    return [
      // Prénom + spécial + année + initiale du nom
      `${cap(first)}${specials[0]}${yr}${last[0].toUpperCase()}`,
      // Initiales + mot mémorable + nombre
      `${initials}#Apprendre${rand3()}`,
      // Prénom inversé + spécial + 3 chiffres
      `${cap(rev(first))}${specials[2]}${rand3()}`,
      // Mix : 3 lettres prénom + 3 lettres nom + spécial + année court
      `${cap(first.slice(0, 3))}${cap(last.slice(0, 3))}${specials[3]}${String(yr).slice(-2)}`,
    ];
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registrationPending.current) return;
    setError(null);
    const { score } = getPasswordStrength(registerPassword);
    if (score < 4) {
      setError("Mot de passe insuffisant. Vous devez avoir : 8 caractères minimum, une majuscule, un chiffre et un caractère spécial (@, #, !, …).");
      setShowPasswordSuggestions(true);
      return;
    }
    registrationPending.current = true;
    setRegistrationStage("Création de votre compte… veuillez patienter.");
    try {
      const formData = new FormData();
      formData.append("username", registerUsername);
      formData.append("email", registerEmail);
      formData.append("password", registerPassword);
      formData.append("nom_complet", registerNomComplet);
      formData.append("ecole", registerEcole);
      formData.append("est_enseignant", registerEstEnseignant ? "true" : "false");
      if (!registerEstEnseignant) formData.append("code_manuel", registerCodeManuel);
      if (!registerEstEnseignant && registerReferralCode.trim()) formData.append("referral_code", registerReferralCode.trim());
      if (registerClasseId) formData.append("classe_id", registerClasseId);
      if (registerPhoto) formData.append("photo", registerPhoto);

      const r = await api.post("register/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRegistrationStage("Préparation de votre espace… veuillez patienter.");
      saveTokens(r.data);
      setWelcomeBonus(r.data.welcome_bonus?.granted ? r.data.welcome_bonus : null);
      if (isAndroidPlayConsumptionOnly && r.data.is_enseignant) {
        setRegisterPassword("");
        restrictAndroidTeacher();
        return;
      }
      // Même coexistence JWT/fallback que lors de la connexion.
      const teacherSpaceOpened = r.data.redirect_to && r.data.is_enseignant;
      if (teacherSpaceOpened && !isWindowsApp && !isAndroidDirect) {
        window.location.href = teacherTokenLoginUrl(r.data.access);
        return;
      }
      setRegisterPassword("");
      const profile = await loadProfile({ openTeacherSpace: !teacherSpaceOpened });
      if (isAndroidApp && !profile) return;
      setShowAuthModal(false);
      // Comptable → pas de dashboard élève, l'admin panel s'affiche via user.is_comptable
      if (r.data.is_comptable || r.data.is_community_manager || r.data.is_secretaire || r.data.is_brand_ambassador) return;
      await loadDashboard();
      await loadManuals();
      await loadModules();
      // La visite guidée animée de l'espace se lance d'elle-même au premier accès.
    } catch (err) {
      setError(err.response?.data?.detail || "Impossible de s'inscrire. Vérifiez votre connexion et réessayez.");
    } finally {
      registrationPending.current = false;
      setRegistrationStage("");
    }
  };

  const handleLogout = async () => {
    try { await api.post("logout/"); } catch {}
    await purgerHorsConnexion();
    clearTokens();
    setUser(null);
    setDashboardSummary(null);
    setModules([]);
    if (isWindowsApp) {
      setError(null);
      setShowAuthModal(false);
      setWindowsSection("accueil");
      window.history.replaceState(
        { ...window.history.state, quickLearningSection: "accueil" },
        document.title
      );
    }
    loadManuals();
  };

  const Logo = () => (
    <div className="ql-logo">
      <img src="/static/logo.png" alt="Quick Learning" className="ql-logo-img" />
    </div>
  );

  const AndroidTeacherRestriction = () => (
    <main className="ql-android-teacher-restriction">
      <section className="ql-android-teacher-card" role="alert" aria-live="assertive">
        <Logo />
        <div className="ql-android-teacher-icon" aria-hidden="true">
          <i className="bi bi-mortarboard-fill"></i>
        </div>
        <h1>Espace enseignant</h1>
        <p>
          L’espace enseignant n’est pas disponible dans l’application Android.
          Veuillez utiliser l’application Windows ou le site web Quick Learning.
        </p>
        {androidTeacherSiteError && (
          <div className="alert alert-danger" role="alert">{androidTeacherSiteError}</div>
        )}
        <div className="ql-android-teacher-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              setAndroidTeacherSiteError(null);
              try {
                await openAndroidTeacherSite();
              } catch {
                setAndroidTeacherSiteError("Impossible d’ouvrir le navigateur système.");
              }
            }}
          >
            <i className="bi bi-box-arrow-up-right me-2"></i>Ouvrir le site web
          </button>
          <button
            type="button"
            className="btn btn-outline-navy"
            onClick={() => {
              clearTokens();
              setUser(null);
              setAndroidTeacherRestricted(false);
              setAndroidTeacherSiteError(null);
              setAuthTab("login");
              setShowAuthModal(true);
            }}
          >
            <i className="bi bi-arrow-left me-2"></i>Retour à la connexion
          </button>
        </div>
      </section>
    </main>
  );

  if ((isWindowsApp || isAndroidApp) && sessionChecking) {
    return (
      <div className={isAndroidApp ? "ql-android-splash" : "ql-windows-splash"} role="status" aria-live="polite">
        <img src="/static/logo.png" alt="Quick Learning" className={isAndroidApp ? "ql-android-splash-logo" : "ql-windows-splash-logo"} />
        <div className={isAndroidApp ? "ql-android-splash-spinner" : "ql-windows-splash-spinner"} aria-hidden="true"></div>
        <p>Ouverture de votre espace…</p>
      </div>
    );
  }

  if (window.location.pathname.startsWith("/aide")) {
    return <HelpCenter />;
  }

  if (window.location.pathname.startsWith("/profil-soutien")) {
    return <TeacherSupportProfile onBack={() => { window.location.href = "/epreuves/dashboard/"; }} />;
  }

  if (shouldRenderSupportPortal({ pathname: window.location.pathname, windows: isWindowsApp, android: isAndroidApp })) {
    return <><ParentTeacherPortal /><AIAssistantComingSoon /></>;
  }

  if (shouldRenderReferralPortal({ pathname: window.location.pathname })) {
    return <><StudentReferralPortal /><AIAssistantComingSoon /></>;
  }

  if (isAndroidPlayConsumptionOnly && androidTeacherRestricted) {
    return <><AndroidTeacherRestriction /><AIAssistantComingSoon /></>;
  }

  /* ── PAGE D'ACCUEIL (non connecté) ── */
  if (!user) {
    return (
      <div className={isWindowsApp ? "ql-windows-landing" : isAndroidApp ? "ql-android-landing" : undefined}>
        {/* ── En-tête du site (Web) : logo à gauche, liens puis téléchargements et compte ── */}
        {!isWindowsApp && !isAndroidApp && (
        <nav className="ql-navbar ql-accueil-nav">
          <div className="container ql-accueil-entete">
            <a href="#accueil" className="ql-accueil-logo" aria-label="Quick Learning — accueil">
              <img src="/static/logo.png" alt="Quick Learning — Apprendre n'a jamais été aussi aisé" />
            </a>
            <div className="ql-accueil-entete-droite">
              <div className="ql-accueil-liens">
                <a href="#accueil" className="active">Accueil</a>
                <a href="#manuel">Le manuel</a>
                <a href="#features">Fonctionnalités</a>
                <a href="#tarifs">Tarifs</a>
                <a href="#apropos">À propos</a>
                <a href="#contact">Contact</a>
              </div>
              <div className="ql-accueil-barre">
                <div className="ql-accueil-telecharger">
                  <span>Télécharger l’application</span>
                  <a href="/telecharger/windows/" className="ql-accueil-btn" title="Télécharger Quick Learning pour Windows"><i className="bi bi-windows"></i>Windows</a>
                  <a href="/telecharger/mac/" className="ql-accueil-btn" title="Télécharger Quick Learning pour Mac"><i className="bi bi-apple"></i>Mac</a>
                  <a href="/telecharger/android/" className="ql-accueil-btn" title="Télécharger Quick Learning pour Android"><i className="bi bi-android2"></i>Android</a>
                </div>
                <div className="ql-accueil-compte">
                  <a className="ql-accueil-btn" href="/aide"><i className="bi bi-question-circle"></i>Aide</a>
                  {parentConnecte
                    ? <a className="ql-accueil-btn" href="/soutien/mes-demandes"><i className="bi bi-people"></i>Mon espace parent</a>
                    : <button type="button" className="ql-accueil-btn" onClick={openLogin}><i className="bi bi-person"></i>Se connecter</button>}
                  <button type="button" className="ql-accueil-btn ql-accueil-btn--orange" onClick={openLogin}><i className="bi bi-key"></i>Activer mon manuel</button>
                </div>
              </div>
            </div>
          </div>
        </nav>
        )}

        {/* ── Navbar des applications Windows / Android ── */}
        {(isWindowsApp || isAndroidApp) && (
        <nav className={`ql-navbar${isWindowsApp ? " ql-windows-navbar" : isAndroidApp ? " ql-android-navbar" : ""}`}>
          <div className={isWindowsApp || isAndroidApp ? "container d-flex align-items-center justify-content-between" : "container ql-navbar-inner"}>
            {isWindowsApp || isAndroidApp ? (
              <button
                type="button"
                className={isAndroidApp ? "ql-android-logo-button" : "ql-windows-logo-button"}
                onClick={() => isAndroidApp ? navigateAndroidSection("accueil") : navigateWindowsSection("accueil")}
                aria-label="Revenir à l'accueil"
              >
                <Logo />
              </button>
            ) : (
              <Logo />
            )}
            {isWindowsApp ? (
              <div className="ql-windows-nav" aria-label="Navigation principale">
                {[
                  ["accueil", "Accueil"],
                  ["manuel", "Le manuel"],
                  ["decouverte", "Découverte"],
                  ["fonctionnalites", "Fonctionnalités"],
                  ["tarifs", "Tarifs"],
                  ["apropos", "À propos"],
                  ["contact", "Contact"],
                ].map(([section, label]) => (
                  <button
                    type="button"
                    key={section}
                    className={windowsSection === section ? "active" : ""}
                    onClick={() => navigateWindowsSection(section)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : isAndroidApp ? (
              <div className="ql-android-menu-wrap">
                <button
                  type="button"
                  className="ql-android-menu-toggle"
                  aria-label={androidMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                  aria-expanded={androidMenuOpen}
                  aria-controls="ql-android-navigation"
                  onClick={() => setAndroidMenuOpen((open) => !open)}
                >
                  <i className={`bi ${androidMenuOpen ? "bi-x-lg" : "bi-list"}`}></i>
                </button>
                <div
                  id="ql-android-navigation"
                  className={`ql-android-nav${androidMenuOpen ? " open" : ""}`}
                  aria-label="Navigation principale"
                >
                  {[
                    ["accueil", "Accueil"],
                    ["manuel", "Le manuel"],
                    ["decouverte", "Découverte"],
                    ["fonctionnalites", "Fonctionnalités"],
                    ["tarifs", "Tarifs"],
                    ["apropos", "À propos"],
                    ["contact", "Contact"],
                  ].map(([section, label]) => (
                    <button
                      type="button"
                      key={section}
                      className={androidSection === section ? "active" : ""}
                      onClick={() => navigateAndroidSection(section)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="ql-nav-links d-none d-lg-flex">
                <a href="#accueil" className="active">Accueil</a>
                <a href="#manuel">Le manuel</a>
                <a href="#features">Fonctionnalités</a>
                <a href="#tarifs">Tarifs</a>
                <a href="#apropos">À propos</a>
                <a href="#contact">Contact</a>
              </div>
            )}
            <div className="ql-nav-actions">
              {!isWindowsApp && !isAndroidApp && (
                <>
                  <a href="/telecharger/windows/" className="btn btn-outline-navy px-3 py-2 d-inline-flex align-items-center" title="Télécharger Quick Learning pour Windows" aria-label="Windows">
                    <i className="bi bi-windows"></i><span className="d-none d-md-inline ms-1">Windows</span>
                  </a>
                  <a href="/telecharger/mac/" className="btn btn-outline-navy px-3 py-2 d-inline-flex align-items-center" title="Télécharger Quick Learning pour Mac" aria-label="Mac">
                    <i className="bi bi-apple"></i><span className="d-none d-md-inline ms-1">Mac</span>
                  </a>
                  <a href="/telecharger/android/" className="btn btn-outline-navy px-3 py-2 d-inline-flex align-items-center" title="Télécharger Quick Learning pour Android" aria-label="Android">
                    <i className="bi bi-android2"></i><span className="d-none d-md-inline ms-1">Android</span>
                  </a>
                </>
              )}
              <a className="btn btn-outline-navy px-3 py-2" href="/aide">
                <i className="bi bi-question-circle me-1"></i>Aide
              </a>
              {parentConnecte ? (
                <a className="btn btn-outline-navy px-3 py-2" href="/soutien/mes-demandes">
                  <i className="bi bi-people me-1"></i>Mon espace parent
                </a>
              ) : (
                <button className="btn btn-outline-navy px-3 py-2" onClick={openLogin}>
                  <i className="bi bi-person me-1"></i>Se connecter
                </button>
              )}
              <button className="btn btn-orange px-3 py-2" onClick={openLogin}>
                <i className="bi bi-key me-1"></i>Activer mon manuel
              </button>
            </div>
          </div>
        </nav>
        )}

        {/* ── Accueil ── */}
        {showLandingSection("accueil") && (
        <>
        <section className="ql-accueil-hero" id="accueil">
          <DiaporamaAccueil />
          <div className="container">
            <div className="ql-accueil-texte">
              <p className="ql-accueil-sur-titre">LE MANUEL PAPIER + LE NUMÉRIQUE</p>
              <h1 className="ql-accueil-titre">
                <span>Comprends tes cours.</span>
                <span>Entraîne-toi.</span>
                <span className="ql-accueil-titre-orange">Progresse.</span>
              </h1>
              <p className="ql-accueil-sous-titre">
                Des manuels, des exercices et des vidéos de correction pour avancer à ton rythme.
              </p>
              <div className="ql-accueil-boutons">
                <button type="button" className="ql-accueil-cta ql-accueil-cta--orange"
                  onClick={() => (isWindowsApp ? navigateWindowsSection("decouverte") : isAndroidApp ? navigateAndroidSection("decouverte")
                    : document.getElementById("decouverte")?.scrollIntoView({ behavior: "smooth" }))}>
                  Découvrir Quick Learning<i className="bi bi-arrow-right"></i>
                </button>
                <button type="button" className="ql-accueil-cta" onClick={openLogin}>
                  <i className="bi bi-key"></i>Activer mon manuel
                </button>
              </div>
              <ul className="ql-accueil-points">
                <li>Cours</li>
                <li>Exercices</li>
                <li>Corrections vidéo</li>
                <li>Lives vidéo</li>
                <li>Épreuves des établissements</li>
                <li>Assistance par des enseignants</li>
              </ul>
            </div>
          </div>
        </section>
        <div className="container-fluid px-0">
          <div className="ql-accueil-enseignants">
            <i className="bi bi-people-fill ql-accueil-enseignants-icone" aria-hidden="true"></i>
            <strong>Enseignants : préparez vos épreuves avec Quick Learning</strong>
            <button type="button" className="ql-accueil-enseignants-lien" onClick={openLogin}>Découvrir le générateur<i className="bi bi-arrow-right"></i></button>
          </div>
        </div>
        </>
        )}

        {/* ── Vidéo de démonstration ── */}
        {showLandingSection("decouverte") && (
        <section className="ql-section-pad" id="decouverte" style={{ background:"#f8faff" }}>
          <div className="container">
            <div className="ql-section-header">
              <span style={{ display:"inline-block", background:"#f5820d18", color:"#e07209", fontWeight:700, fontSize:"0.8rem", padding:"4px 16px", borderRadius:20, letterSpacing:"0.08em", marginBottom:14 }}>
                DÉCOUVREZ LA PLATEFORME
              </span>
              <h2 style={{ fontSize:"clamp(1.6rem,4vw,2.2rem)", fontWeight:800, color:"#1e3a5f", marginBottom:12 }}>
                Comment fonctionne Quick Learning ?
              </h2>
              <p style={{ color:"#64748b", fontSize:"0.95rem", maxWidth:560, margin:"0 auto" }}>
                Une courte vidéo pour découvrir les manuels numériques, les exercices, et le générateur d'épreuves pour les enseignants.
              </p>
            </div>

            {videoDemos.length === 0 ? (
              <div style={{ maxWidth:860, margin:"0 auto", borderRadius:18, overflow:"hidden", boxShadow:"0 12px 40px #1e3a5f1f" }}>
                <div style={{ position:"relative", paddingTop:"56.25%", background:"linear-gradient(135deg,#1e3a5f,#2d5a8e)" }}>
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:"#fff", gap:12 }}>
                    <div style={{ width:64, height:64, borderRadius:"50%", background:"#ffffff22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.8rem" }}>
                      <i className="bi bi-play-fill"></i>
                    </div>
                    <p style={{ margin:0, fontWeight:600, fontSize:"0.95rem" }}>Vidéo de démonstration bientôt disponible</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="ql-demo-video-grid">
                {videoDemos.map(video => {
                  const ytId = getYouTubeId(video.url);
                  return (
                    <button key={video.id} type="button" className="ql-demo-video-card" onClick={() => setOpenVideoDemo(video)}>
                      <span className="ql-demo-video-thumb" style={{ backgroundImage: `url(https://img.youtube.com/vi/${ytId}/hqdefault.jpg)` }}>
                        <span className="ql-demo-video-play"><i className="bi bi-play-fill"></i></span>
                      </span>
                      <span className="ql-demo-video-title">{video.titre || "Vidéo de démonstration"}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {openVideoDemo && (
              <div className="ql-demo-video-lightbox" role="dialog" aria-modal="true" onClick={() => setOpenVideoDemo(null)}>
                <div className="ql-demo-video-lightbox-inner" onClick={e => e.stopPropagation()}>
                  <button type="button" className="ql-demo-video-lightbox-close" onClick={() => setOpenVideoDemo(null)} aria-label="Fermer">×</button>
                  <div style={{ position:"relative", paddingTop:"56.25%" }}>
                    <iframe
                      style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
                      src={urlLecteurYoutube(getYouTubeId(openVideoDemo.url))}
                      title={openVideoDemo.titre || "Découvrir Quick Learning"}
                      frameBorder="0"
                      allow={AUTORISATIONS_LECTEUR}
                      allowFullScreen
                    />
                  </div>
                  {openVideoDemo.titre && <p className="ql-demo-video-lightbox-title">{openVideoDemo.titre}</p>}
                </div>
              </div>
            )}
          </div>
        </section>
        )}

        {/* ── Nos Manuels ── */}
        {showLandingSection("manuel") && (
        <section className="ql-manuels-section" id="manuel">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="ql-section-title">Nos Manuels disponibles</h2>
              <p className="ql-section-sub">
                Choisissez votre manuel, activez votre code et accédez à tout le contenu numérique.
              </p>
            </div>
            {(() => {
              const visibleManuals = asArray(manuals).filter(m => m.image_url);
              if (visibleManuals.length === 0) {
                return (
                  <div className="text-center py-5">
                    <i className="bi bi-book-half text-muted" style={{ fontSize: "3rem" }}></i>
                    <p className="text-muted mt-3">Aucun manuel disponible pour le moment.</p>
                  </div>
                );
              }
              const renderCard = (manuel) => (
                <div key={manuel.id} className="col-md-6 col-lg-4">
                  <div className="ql-manuel-card">
                    <div className="ql-manuel-card-cover">
                      <img
                        src={manuel.image_url}
                        alt={manuel.title}
                        className="ql-manuel-cover-img"
                      />
                      <span className="ql-manuel-level-badge">{manuel.level}</span>
                    </div>
                    <div className="ql-manuel-card-body">
                      <h4 className="ql-manuel-title">{manuel.title}</h4>
                      {manuel.description && (
                        <p className="ql-manuel-desc">{manuel.description}</p>
                      )}
                    </div>
                    <div className="ql-manuel-card-footer">
                      <div className="d-flex gap-2 align-items-center mb-3">
                        <span className="ql-manuel-badge">
                          <i className="bi bi-layers me-1"></i>25 chapitres
                        </span>
                        <span className="ql-manuel-badge">
                          <i className="bi bi-play-circle me-1"></i>+10h vidéos
                        </span>
                      </div>
                      <button className="btn btn-orange w-100" onClick={openLogin}>
                        <i className="bi bi-key me-2"></i>Activer mon manuel
                      </button>
                    </div>
                  </div>
                </div>
              );

              if (showAllManuals) {
                return (
                  <>
                    <div className="row g-4 justify-content-center">
                      {visibleManuals.map(renderCard)}
                    </div>
                    <div className="text-center mt-4">
                      <button
                        className="btn btn-outline-navy px-4"
                        onClick={() => setShowAllManuals(false)}
                      >
                        <i className="bi bi-chevron-up me-2"></i>Voir moins
                      </button>
                    </div>
                  </>
                );
              }

              return (
                <div className="row g-4 justify-content-center align-items-stretch">
                  {renderCard(visibleManuals[0])}
                  <div className="col-md-6 col-lg-4">
                    <button
                      onClick={() => setShowAllManuals(true)}
                      style={{ width:"100%", height:"100%", minHeight:260, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, background:"#fff", border:"2px dashed #cbd5e1", borderRadius:16, cursor:"pointer", transition:"transform 0.2s, border-color 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.borderColor="#f5820d"; }}
                      onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.borderColor="#cbd5e1"; }}
                    >
                      <div style={{ width:56, height:56, borderRadius:"50%", background:"#f5820d18", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        <i className="bi bi-grid-3x3-gap-fill" style={{ color:"#f5820d", fontSize:"1.5rem" }}></i>
                      </div>
                      <span style={{ fontWeight:800, color:"#1e3a5f", fontSize:"1.05rem" }}>Voir tous les manuels</span>
                      {visibleManuals.length > 1 && (
                        <span style={{ fontSize:"0.85rem", color:"#64748b" }}>+{visibleManuals.length - 1} autres manuels disponibles</span>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
        )}

        {/* ── Bandeau fonctionnalités ── */}
        {showLandingSection("fonctionnalites") && (
        <section id="features" className="ql-section-pad">
          <div className="container">
            <div className="ql-section-header">
              <span style={{ display:"inline-block", background:"#3b82f618", color:"#2563eb", fontWeight:700, fontSize:"0.8rem", padding:"4px 16px", borderRadius:20, letterSpacing:"0.08em", marginBottom:14 }}>
                FONCTIONNALITÉS
              </span>
              <h2 style={{ fontSize:"clamp(1.6rem,4vw,2.3rem)", fontWeight:800, color:"#1e3a5f", marginBottom:12 }}>
                Tout ce qu'il faut pour réussir
              </h2>
              <p style={{ color:"#64748b", fontSize:"0.97rem", maxWidth:560, margin:"0 auto" }}>
                Quick Learning réunit en un seul endroit tous les outils dont un élève camerounais a besoin pour exceller aux examens officiels.
              </p>
            </div>

            <div className="ql-feat-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:24 }}>
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className="ql-feat-card-item"
                  style={{ background:"#fff", borderRadius:18, padding:"28px 24px", border:"1.5px solid #e2e8f0", boxShadow:"0 2px 12px #0001", transition:"transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 10px 32px #1e3a5f14"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 12px #0001"; }}
                >
                  <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
                    <div style={{ width:50, height:50, borderRadius:14, background:f.color+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <i className={`bi ${f.icon}`} style={{ color:f.color, fontSize:"1.4rem" }}></i>
                    </div>
                    <div>
                      <span style={{ display:"inline-block", background:f.color+"14", color:f.color, fontSize:"0.68rem", fontWeight:700, padding:"2px 9px", borderRadius:10, letterSpacing:"0.06em" }}>{f.tag}</span>
                      <div style={{ fontWeight:800, color:"#1e3a5f", fontSize:"0.97rem", marginTop:3, lineHeight:1.3 }}>{f.title}</div>
                    </div>
                  </div>
                  <p style={{ fontSize:"0.875rem", color:"#475569", lineHeight:1.75, margin:0 }}>{f.desc}</p>
                </div>
              ))}
            </div>

            <div style={{ textAlign:"center", marginTop:44 }}>
              <button
                onClick={openLogin}
                style={{ padding:"13px 36px", background:"linear-gradient(135deg,#1e3a5f,#2d5a8e)", color:"#fff", border:"none", borderRadius:12, fontWeight:700, fontSize:"0.95rem", cursor:"pointer", boxShadow:"0 4px 20px #1e3a5f33" }}
                onMouseEnter={e => e.currentTarget.style.opacity="0.88"}
                onMouseLeave={e => e.currentTarget.style.opacity="1"}
              >
                <i className="bi bi-rocket-takeoff-fill me-2"></i>Découvrir la plateforme
              </button>
            </div>
          </div>
        </section>
        )}

        {/* ── Statistiques ── */}
        {showLandingSection("apropos") && (
        <section className="ql-stats">
          <div className="container">
            <div className="row g-0">
              {STATS.map((s, i) => (
                <div key={i} className="col">
                  <div className="ql-stat-item">
                    <i className={`bi ${s.icon}`}></i>
                    <span className="ql-stat-number">{s.number}</span>
                    <span className="ql-stat-label">{s.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* ── Tarifs ── */}
        {showLandingSection("tarifs") && (
        <section id="tarifs" className="ql-section-pad" style={{ background:"linear-gradient(160deg,#f0f4ff 0%,#faf8ff 60%,#fff7ed 100%)" }}>
          <div className="container">

            {/* En-tête persuasif */}
            <div className="ql-section-header" style={{ maxWidth:680, margin:"0 auto 56px" }}>
              <span style={{ display:"inline-block", background:"#f5820d20", color:"#f5820d", fontWeight:700, fontSize:"0.8rem", padding:"4px 16px", borderRadius:20, letterSpacing:"0.08em", marginBottom:16 }}>
                TARIFS ACCESSIBLES
              </span>
              <h2 style={{ fontSize:"clamp(1.7rem,4vw,2.4rem)", fontWeight:800, color:"#1e3a5f", marginBottom:16, lineHeight:1.2 }}>
                Investissez dans votre réussite scolaire
              </h2>
              <p style={{ fontSize:"1.05rem", color:"#475569", lineHeight:1.7, marginBottom:24 }}>
                Pour <strong>moins de 100 FCFA par jour</strong>, accédez à l'intégralité des cours, vidéos, QCM et exercices de votre manuel — disponibles <strong>24h/24, 7j/7</strong>, même sans connexion internet stable.
              </p>
              <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", gap:"12px 24px" }}>
                {[
                  { icon:"bi-play-circle-fill", text:"Vidéos explicatives illimitées" },
                  { icon:"bi-patch-check-fill", text:"QCM & exercices corrigés" },
                  { icon:"bi-chat-dots-fill",   text:"Messagerie avec les enseignants" },
                  { icon:"bi-graph-up-arrow",   text:"Suivi de ta progression" },
                  { icon:"bi-lightning-fill",   text:"Activation immédiate" },
                  { icon:"bi-shield-lock-fill", text:"Paiement 100 % sécurisé" },
                ].map((b, i) => (
                  <span key={i} style={{ display:"flex", alignItems:"center", gap:6, fontSize:"0.85rem", color:"#1e3a5f", fontWeight:600 }}>
                    <i className={`bi ${b.icon}`} style={{ color:"#f5820d" }}></i>{b.text}
                  </span>
                ))}
              </div>
            </div>

            {/* Cartes de plans */}
            <div className="ql-plans-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:20, marginBottom:48 }}>
              {PLANS.map(p => (
                <div
                  key={p.days}
                  style={{
                    position:"relative", borderRadius:16, padding:"28px 20px 24px",
                    background: p.popular ? "linear-gradient(135deg,#fff4e6,#fff)" : "#fff",
                    border: p.popular ? "2px solid #f5820d" : "1.5px solid #e2e8f0",
                    boxShadow: p.popular ? "0 8px 32px #f5820d22" : "0 2px 12px #0001",
                    display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center",
                    transform: p.popular ? "scale(1.04)" : "scale(1)",
                    transition:"transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform="scale(1.06)"; e.currentTarget.style.boxShadow="0 12px 36px #1e3a5f22"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = p.popular?"scale(1.04)":"scale(1)"; e.currentTarget.style.boxShadow = p.popular?"0 8px 32px #f5820d22":"0 2px 12px #0001"; }}
                >
                  {p.popular && (
                    <span style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)", background:"#f5820d", color:"#fff", fontSize:"0.72rem", fontWeight:700, padding:"3px 14px", borderRadius:20, letterSpacing:"0.06em" }}>
                      ⭐ Populaire
                    </span>
                  )}
                  <span style={{ display:"inline-block", background:p.color+"18", color:p.color, fontWeight:700, fontSize:"0.75rem", padding:"3px 12px", borderRadius:12, marginBottom:10, letterSpacing:"0.05em" }}>
                    {p.badge}
                  </span>
                  <div style={{ fontSize:"1.4rem", fontWeight:800, color:"#1e3a5f", marginBottom:4 }}>{p.label}</div>
                  <div style={{ fontSize:"1.9rem", fontWeight:900, color: p.popular?"#f5820d":"#1e3a5f", marginBottom:2 }}>
                    {p.price.toLocaleString("fr-FR")} <span style={{ fontSize:"1rem", fontWeight:600 }}>FCFA</span>
                  </div>
                  <div style={{ fontSize:"0.78rem", color:"#94a3b8", marginBottom:20 }}>
                    soit {Math.round(p.price / p.days)} FCFA/jour
                  </div>
                  <button
                    onClick={openLogin}
                    style={{
                      width:"100%", padding:"10px 0", border:"none", borderRadius:10, cursor:"pointer",
                      fontWeight:700, fontSize:"0.88rem",
                      background: p.popular ? "#f5820d" : "#1e3a5f",
                      color:"#fff",
                      transition:"opacity 0.2s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity="0.85"}
                    onMouseLeave={e => e.currentTarget.style.opacity="1"}
                  >
                    Commencer →
                  </button>
                </div>
              ))}
            </div>

            {/* Méthodes de paiement */}
            <div style={{ textAlign:"center", marginBottom:28 }}>
              <p style={{ fontSize:"0.85rem", color:"#64748b", fontWeight:600, marginBottom:12 }}>MÉTHODES DE PAIEMENT ACCEPTÉES</p>
              <div style={{ display:"flex", justifyContent:"center", gap:16, flexWrap:"wrap" }}>
                {[
                  { icon:"bi-phone-fill", label:"MTN Mobile Money", color:"#fbbf24", bg:"#fffbeb" },
                  { icon:"bi-phone-fill", label:"Orange Money",      color:"#f97316", bg:"#fff7ed" },
                ].map((m, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:8, background:m.bg, border:`1.5px solid ${m.color}44`, borderRadius:10, padding:"8px 18px" }}>
                    <i className={`bi ${m.icon}`} style={{ color:m.color, fontSize:"1.1rem" }}></i>
                    <span style={{ fontWeight:600, fontSize:"0.85rem", color:"#1e3a5f" }}>{m.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Garanties */}
            <div style={{ textAlign:"center" }}>
              <p style={{ fontSize:"0.82rem", color:"#94a3b8" }}>
                <i className="bi bi-lightning-charge-fill me-1" style={{ color:"#22c55e" }}></i>Activation immédiate après paiement
                <span style={{ margin:"0 12px" }}>•</span>
                <i className="bi bi-arrow-repeat me-1" style={{ color:"#3b82f6" }}></i>Aucun engagement, résiliable à tout moment
                <span style={{ margin:"0 12px" }}>•</span>
                <i className="bi bi-shield-check-fill me-1" style={{ color:"#f5820d" }}></i>Transaction sécurisée via CinetPay
              </p>
              <p style={{ fontSize:"0.88rem", color:"#475569", marginTop:10, fontStyle:"italic" }}>
                "Des milliers d'élèves camerounais font déjà confiance à Quick Learning pour exceller aux examens. Et toi ?"
              </p>
              <button
                onClick={openLogin}
                style={{ marginTop:16, padding:"14px 40px", background:"linear-gradient(135deg,#1e3a5f,#2d5a8e)", color:"#fff", border:"none", borderRadius:12, fontWeight:700, fontSize:"1rem", cursor:"pointer", boxShadow:"0 4px 20px #1e3a5f33" }}
                onMouseEnter={e => e.currentTarget.style.opacity="0.9"}
                onMouseLeave={e => e.currentTarget.style.opacity="1"}
              >
                <i className="bi bi-key-fill me-2"></i>Activer mon accès maintenant
              </button>
            </div>

          </div>
        </section>
        )}

        {/* ── CTA Soutien scolaire ── */}
        {!isWindowsApp && !isAndroidApp && (
        <section className="ql-section-pad" style={{ background:"#f5f8ff", textAlign:"center" }}>
          <div className="container">
            <h2 style={{ marginBottom: "0.75rem" }}>Retrouvez les meilleurs professeurs pour accompagner vos enfants</h2>
            <SoutienTeacherPreview />
            <a href="/soutien" className="btn btn-orange px-4 py-2">Cliquez ici</a>
          </div>
        </section>
        )}

        {/* ── À propos ── */}
        {showLandingSection("apropos") && (
        <section id="apropos" className="ql-section-pad" style={{ background:"#fff" }}>
          <div className="container">

            {/* Badge + titre */}
            <div style={{ textAlign:"center", marginBottom:56 }}>
              <span style={{ display:"inline-block", background:"#1e3a5f15", color:"#1e3a5f", fontWeight:700, fontSize:"0.8rem", padding:"4px 16px", borderRadius:20, letterSpacing:"0.08em", marginBottom:16 }}>
                NOTRE HISTOIRE
              </span>
              <h2 style={{ fontSize:"clamp(1.7rem,4vw,2.4rem)", fontWeight:800, color:"#1e3a5f", marginBottom:14 }}>
                Une vision née de l'excellence
              </h2>
              <p style={{ fontSize:"1rem", color:"#64748b", maxWidth:560, margin:"0 auto" }}>
                Quick Learning est la rencontre entre l'ingénierie de haut niveau et la passion de transmettre le savoir.
              </p>
            </div>

            {/* Biographie + photo */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center", marginBottom:64 }} className="ql-about-grid">

              {/* Photo / carte fondateur */}
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:20 }}>
                <div className="ql-founder-frame">
                  {/* Cadre décoratif derrière */}
                  <div className="ql-founder-shadow" />
                  {/* Photo */}
                  <div className="ql-founder-photo">
                    <img src="/static/fondateur.jpg" alt="Ing. Borice FOTSO NOUMSI" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"center top" }} />
                  </div>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontWeight:800, fontSize:"1.15rem", color:"#1e3a5f" }}>Ing. Borice FOTSO NOUMSI</div>
                  <div style={{ fontSize:"0.85rem", color:"#f5820d", fontWeight:600, marginTop:2 }}>Fondateur & CEO — Quick Learning</div>
                  <div style={{ fontSize:"0.8rem", color:"#64748b", marginTop:6 }}>Ingénieur Aéronautique · Docteur en formation · Prof. de Maths & Physique</div>
                  <div style={{ display:"flex", justifyContent:"center", gap:10, marginTop:12, flexWrap:"wrap" }}>
                    {["AEROSUP Casablanca", "Master UY1", "Safran", "Airbus"].map((b, i) => (
                      <span key={i} style={{ background:"#f0f4ff", color:"#1e3a5f", fontSize:"0.72rem", fontWeight:700, padding:"3px 10px", borderRadius:10 }}>{b}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Texte biographie */}
              <div>
                <p style={{ fontSize:"1rem", color:"#334155", lineHeight:1.85, marginBottom:18 }}>
                  Né en <strong>1997 au Cameroun</strong>, <strong>Ing. Borice FOTSO NOUMSI</strong> a toujours cru que l'excellence académique ne devrait jamais être une question de moyens. Diplômé ingénieur en <strong>aéronautique de l'École AEROSUP de Casablanca</strong> — l'une des meilleures écoles d'ingénierie du continent africain — et titulaire d'un <strong>Master de l'Université de Yaoundé I</strong> (2019), il a eu l'honneur de collaborer avec des géants mondiaux tels que <strong>Safran</strong> et <strong>Airbus</strong>.
                </p>
                <p style={{ fontSize:"1rem", color:"#334155", lineHeight:1.85, marginBottom:18 }}>
                  Malgré un parcours international brillant, son regard est resté tourné vers une seule réalité : <strong>des milliers d'élèves camerounais échouent chaque année aux examens officiels</strong>, non par manque d'intelligence, mais par manque d'accès à des ressources pédagogiques de qualité.
                </p>
                <p style={{ fontSize:"1rem", color:"#334155", lineHeight:1.85 }}>
                  C'est cette conviction qui a donné naissance à <strong>Quick Learning en 2026</strong> — une plateforme bâtie par un enseignant, pour des élèves, avec la rigueur d'un ingénieur.
                </p>
                <div style={{ marginTop:24, padding:"16px 20px", background:"linear-gradient(135deg,#fff7ed,#fff)", borderLeft:"4px solid #f5820d", borderRadius:"0 12px 12px 0" }}>
                  <p style={{ margin:0, fontStyle:"italic", color:"#1e3a5f", fontWeight:600, fontSize:"0.95rem" }}>
                    "Je voulais qu'un élève de Maroua, de Bafoussam ou de Bertoua ait accès aux mêmes outils qu'un élève de Yaoundé ou Douala. Quick Learning, c'est l'égalité des chances mise en pratique."
                  </p>
                  <p style={{ margin:"8px 0 0", fontSize:"0.78rem", color:"#f5820d", fontWeight:700 }}>— Ing. Borice FOTSO NOUMSI, Fondateur</p>
                </div>
              </div>
            </div>

            {/* Mission + valeurs */}
            <div style={{ background:"linear-gradient(135deg,#f0f4ff,#faf8ff)", borderRadius:20, padding:"40px 36px", marginBottom:56 }}>
              <div style={{ textAlign:"center", marginBottom:36 }}>
                <h3 style={{ fontSize:"1.5rem", fontWeight:800, color:"#1e3a5f", marginBottom:10 }}>Notre mission</h3>
                <p style={{ fontSize:"1rem", color:"#475569", maxWidth:600, margin:"0 auto", lineHeight:1.7 }}>
                  Démocratiser l'accès à une éducation de qualité au Cameroun en mettant la technologie au service de la réussite scolaire — <strong>pour chaque élève, partout dans le pays.</strong>
                </p>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:20 }}>
                {[
                  { icon:"bi-lightbulb-fill",     color:"#f59e0b", title:"Excellence",      desc:"Des contenus conçus par des experts, alignés sur les programmes officiels camerounais." },
                  { icon:"bi-people-fill",         color:"#3b82f6", title:"Accessibilité",   desc:"Des tarifs adaptés à la réalité économique des familles camerounaises." },
                  { icon:"bi-graph-up-arrow",      color:"#22c55e", title:"Résultats",        desc:"Un accompagnement personnalisé pour transformer chaque élève en lauréat." },
                  { icon:"bi-shield-check-fill",   color:"#8b5cf6", title:"Confiance",        desc:"Une plateforme sécurisée, fiable, disponible à tout moment, même offline." },
                ].map((v, i) => (
                  <div key={i} style={{ background:"#fff", borderRadius:14, padding:"20px 18px", boxShadow:"0 2px 12px #0001", textAlign:"center" }}>
                    <div style={{ width:48, height:48, borderRadius:12, background:v.color+"18", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
                      <i className={`bi ${v.icon}`} style={{ color:v.color, fontSize:"1.4rem" }}></i>
                    </div>
                    <div style={{ fontWeight:700, color:"#1e3a5f", marginBottom:6 }}>{v.title}</div>
                    <div style={{ fontSize:"0.82rem", color:"#64748b", lineHeight:1.6 }}>{v.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Objectifs chiffrés */}
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <h3 style={{ fontSize:"1.3rem", fontWeight:800, color:"#1e3a5f", marginBottom:32 }}>Nos ambitions pour 2026–2027</h3>
              <div style={{ display:"flex", justifyContent:"center", flexWrap:"wrap", gap:32 }}>
                {[
                  { number:"+30 000", label:"élèves sur la plateforme", icon:"bi-mortarboard-fill", color:"#3b82f6" },
                  { number:"95 %",    label:"de taux de réussite aux examens officiels", icon:"bi-trophy-fill", color:"#f5820d" },
                  { number:"10+",     label:"matières disponibles (Maths, Physique, Chimie…)", icon:"bi-book-fill", color:"#22c55e" },
                  { number:"24/7",    label:"accès aux contenus, partout au Cameroun", icon:"bi-wifi", color:"#8b5cf6" },
                ].map((s, i) => (
                  <div key={i} style={{ textAlign:"center", minWidth:160 }}>
                    <div style={{ width:56, height:56, borderRadius:16, background:s.color+"15", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}>
                      <i className={`bi ${s.icon}`} style={{ color:s.color, fontSize:"1.5rem" }}></i>
                    </div>
                    <div style={{ fontSize:"1.8rem", fontWeight:900, color:"#1e3a5f" }}>{s.number}</div>
                    <div style={{ fontSize:"0.8rem", color:"#64748b", maxWidth:140, margin:"4px auto 0" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>
        )}

        {/* ── Témoignages ── */}
        {showLandingSection("apropos") && (
        <section id="temoignages" className="ql-section-pad" style={{ background:"linear-gradient(160deg,#f8faff 0%,#fff7ed 100%)" }}>
          <div className="container">
            <div className="ql-section-header">
              <span style={{ display:"inline-block", background:"#22c55e18", color:"#16a34a", fontWeight:700, fontSize:"0.8rem", padding:"4px 16px", borderRadius:20, letterSpacing:"0.08em", marginBottom:14 }}>
                ILS NOUS FONT CONFIANCE
              </span>
              <h2 style={{ fontSize:"clamp(1.6rem,4vw,2.2rem)", fontWeight:800, color:"#1e3a5f", marginBottom:12 }}>
                Ce que disent nos élèves et nos enseignants
              </h2>
              <p style={{ color:"#64748b", fontSize:"0.95rem", maxWidth:500, margin:"0 auto" }}>
                Des résultats concrets, des mots sincères.
              </p>
            </div>

            <div className="ql-testi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:28 }}>

              {/* Témoignage 1 */}
              <div className="ql-testi-item" style={{ background:"#fff", borderRadius:20, padding:"32px 28px", boxShadow:"0 4px 24px #1e3a5f0f", border:"1.5px solid #e2e8f0", position:"relative" }}>
                <div style={{ position:"absolute", top:20, right:24, fontSize:"3.5rem", color:"#f5820d", lineHeight:1, opacity:0.15, fontFamily:"Georgia,serif", fontWeight:900 }}>"</div>
                {/* Étoiles */}
                <div style={{ marginBottom:16 }}>
                  {[1,2,3,4,5].map(i => <span key={i} style={{ color:"#f59e0b", fontSize:"1rem" }}>★</span>)}
                </div>
                <p style={{ fontSize:"0.95rem", color:"#334155", lineHeight:1.85, marginBottom:24, fontStyle:"italic" }}>
                  "Avant Quick Learning, je passais des heures à relire mes cours sans vraiment tout saisir.
                  Grâce aux vidéos de la plateforme, chaque notion est expliquée avec clarté et des exemples
                  concrets — même les chapitres de Physique que je trouvais impossibles ! Le manuel était déjà
                  un bon outil, mais combiné à la plateforme, c'est une expérience complètement différente.
                  En un seul trimestre, j'ai gagné <strong>+5 points en Physique</strong> et <strong>+4 points en Maths</strong>.
                  Je recommande Quick Learning à tous mes camarades sans hésitation."
                </p>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#1e3a5f,#3b82f6)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:"1.1rem", flexShrink:0 }}>
                    AT
                  </div>
                  <div>
                    <div style={{ fontWeight:700, color:"#1e3a5f", fontSize:"0.92rem" }}>TATSASSE TCHUENKAM Alexandre</div>
                    <div style={{ fontSize:"0.78rem", color:"#64748b", marginTop:2 }}>
                      <i className="bi bi-building me-1" style={{ color:"#f5820d" }}></i>Collège Saint Louise · Élève en classe de Terminale
                    </div>
                  </div>
                </div>
              </div>

              {/* Témoignage 2 */}
              <div className="ql-testi-item" style={{ background:"#fff", borderRadius:20, padding:"32px 28px", boxShadow:"0 4px 24px #1e3a5f0f", border:"1.5px solid #e2e8f0", position:"relative" }}>
                <div style={{ position:"absolute", top:20, right:24, fontSize:"3.5rem", color:"#f5820d", lineHeight:1, opacity:0.15, fontFamily:"Georgia,serif", fontWeight:900 }}>"</div>
                {/* Étoiles */}
                <div style={{ marginBottom:16 }}>
                  {[1,2,3,4,5].map(i => <span key={i} style={{ color:"#f59e0b", fontSize:"1rem" }}>★</span>)}
                </div>
                <p style={{ fontSize:"0.95rem", color:"#334155", lineHeight:1.85, marginBottom:24, fontStyle:"italic" }}>
                  "Quick Learning a totalement changé ma façon de travailler. Les cours sont bien structurés,
                  les vidéos sont simples à suivre et les explications restent en mémoire bien plus longtemps
                  qu'un simple cours écrit. J'ai nettement amélioré mes résultats en <strong>Mathématiques, Chimie
                  et SVT</strong>. Ce que j'apprécie le plus, c'est pouvoir réviser à mon rythme, chez moi,
                  sans dépendre d'un répétiteur. Le manuel en lui-même est déjà excellent, mais avec les
                  ressources numériques de la plateforme, c'est vraiment <strong>un avantage décisif pour le BAC</strong>."
                </p>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#8b5cf6,#c026d3)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:"1.1rem", flexShrink:0 }}>
                    RH
                  </div>
                  <div>
                    <div style={{ fontWeight:700, color:"#1e3a5f", fontSize:"0.92rem" }}>RAFIATOU HINDOU Bala</div>
                    <div style={{ fontSize:"0.78rem", color:"#64748b", marginTop:2 }}>
                      <i className="bi bi-building me-1" style={{ color:"#f5820d" }}></i>Collège Christ Roi de Tsinga · Élève en classe de Première
                    </div>
                  </div>
                </div>
              </div>

              {/* Témoignage 3 — Enseignant */}
              <div className="ql-testi-item" style={{ background:"#fff", borderRadius:20, padding:"32px 28px", boxShadow:"0 4px 24px #1e3a5f0f", border:"1.5px solid #e2e8f0", position:"relative" }}>
                <div style={{ position:"absolute", top:20, right:24, fontSize:"3.5rem", color:"#f5820d", lineHeight:1, opacity:0.15, fontFamily:"Georgia,serif", fontWeight:900 }}>"</div>
                {/* Étoiles */}
                <div style={{ marginBottom:16 }}>
                  {[1,2,3,4,5].map(i => <span key={i} style={{ color:"#f59e0b", fontSize:"1rem" }}>★</span>)}
                </div>
                <p style={{ fontSize:"0.95rem", color:"#334155", lineHeight:1.85, marginBottom:24, fontStyle:"italic" }}>
                  "Le générateur d'épreuves m'a fait gagner un temps précieux. Je choisis les modules et
                  les leçons, le niveau de difficulté, et l'épreuve — avec son corrigé — est prête en
                  quelques minutes, alors que la préparation d'un devoir me prenait auparavant des
                  heures. La mise en page est propre et directement imprimable. C'est devenu un
                  <strong> outil indispensable</strong> dans ma pratique de tous les jours."
                </p>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#f5820d,#fbbf24)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:"1.1rem", flexShrink:0 }}>
                    AA
                  </div>
                  <div>
                    <div style={{ fontWeight:700, color:"#1e3a5f", fontSize:"0.92rem" }}>Adoulaye ADAMOU</div>
                    <div style={{ fontSize:"0.78rem", color:"#64748b", marginTop:2 }}>
                      <i className="bi bi-mortarboard-fill me-1" style={{ color:"#f5820d" }}></i>Enseignant de Mathématiques
                    </div>
                  </div>
                </div>
              </div>

              {/* Témoignage 4 — Enseignante */}
              <div className="ql-testi-item" style={{ background:"#fff", borderRadius:20, padding:"32px 28px", boxShadow:"0 4px 24px #1e3a5f0f", border:"1.5px solid #e2e8f0", position:"relative" }}>
                <div style={{ position:"absolute", top:20, right:24, fontSize:"3.5rem", color:"#f5820d", lineHeight:1, opacity:0.15, fontFamily:"Georgia,serif", fontWeight:900 }}>"</div>
                {/* Étoiles */}
                <div style={{ marginBottom:16 }}>
                  {[1,2,3,4,5].map(i => <span key={i} style={{ color:"#f59e0b", fontSize:"1rem" }}>★</span>)}
                </div>
                <p style={{ fontSize:"0.95rem", color:"#334155", lineHeight:1.85, marginBottom:24, fontStyle:"italic" }}>
                  "Ce que j'apprécie le plus, c'est la fiabilité des épreuves générées : les exercices
                  correspondent vraiment au programme officiel, et je peux varier les sujets d'une classe
                  à l'autre sans effort supplémentaire. Mes élèves progressent également grâce au manuel
                  numérique, qu'ils consultent en dehors des heures de cours. Une plateforme
                  <strong> pensée pour les réalités du terrain</strong>."
                </p>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#22c55e,#0ea5e9)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:800, fontSize:"1.1rem", flexShrink:0 }}>
                    KM
                  </div>
                  <div>
                    <div style={{ fontWeight:700, color:"#1e3a5f", fontSize:"0.92rem" }}>KEDE MVONDO</div>
                    <div style={{ fontSize:"0.78rem", color:"#64748b", marginTop:2 }}>
                      <i className="bi bi-mortarboard-fill me-1" style={{ color:"#f5820d" }}></i>Enseignante de Français
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Note globale */}
            <div style={{ textAlign:"center", marginTop:44 }}>
              <div style={{ display:"inline-flex", alignItems:"center", gap:12, background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:16, padding:"14px 28px", boxShadow:"0 2px 12px #0001" }}>
                <div style={{ fontSize:"2rem", fontWeight:900, color:"#1e3a5f" }}>5.0</div>
                <div>
                  <div>{[1,2,3,4,5].map(i => <span key={i} style={{ color:"#f59e0b", fontSize:"1.1rem" }}>★</span>)}</div>
                  <div style={{ fontSize:"0.75rem", color:"#64748b", marginTop:2 }}>Note moyenne · Nos élèves adorent la plateforme</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ── Contact ── */}
        {showLandingSection("contact") && (
        <section id="contact" className="ql-section-pad" style={{ background:"#1e3a5f" }}>
          <div className="container" style={{ textAlign:"center" }}>
            <h3 style={{ color:"#fff", fontWeight:800, fontSize:"1.6rem", marginBottom:10 }}>Une question ? Contactez-nous</h3>
            <p style={{ color:"#94a3b8", fontSize:"0.95rem", marginBottom:28 }}>Notre équipe est disponible pour vous accompagner dans votre démarche.</p>
            <div style={{ display:"flex", justifyContent:"center", flexWrap:"wrap", gap:20 }}>
              {[
                { icon:"bi-envelope-fill",   label:"contact@quicklearning-cmr.com",         color:"#f5820d" },
                { icon:"bi-whatsapp",        label:"+237693370589 / +237696537926",  color:"#22c55e" },
                { icon:"bi-geo-alt-fill",    label:"Cameroun (national)",        color:"#3b82f6" },
              ].map((c, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, background:"#ffffff10", border:"1px solid #ffffff20", borderRadius:12, padding:"12px 20px" }}>
                  <i className={`bi ${c.icon}`} style={{ color:c.color, fontSize:"1.1rem" }}></i>
                  <span style={{ color:"#e2e8f0", fontSize:"0.88rem", fontWeight:500 }}>{c.label}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 24 }}>
              <ContactForm sombre connecte={!!user} />
            </div>
          </div>
        </section>
        )}

        {/* ── Footer ── */}
        {(!isAndroidApp || androidSection === "contact") && (
        <footer className="ql-footer">
          <p className="mb-0">© 2026 Quick Learning — Fondé par Ing. Borice FOTSO NOUMSI · Cameroun</p>
        </footer>
        )}

        {/* ── Modal connexion / inscription ── */}
        {showAuthModal && (
          <div className="ql-modal-overlay" onClick={closeModal}>
            <div className="ql-modal" onClick={(e) => e.stopPropagation()}>
              <button className="ql-modal-close" onClick={closeModal}>
                <i className="bi bi-x-lg"></i>
              </button>

              <div className="ql-modal-tabs">
                <button
                  className={authTab === "login" ? "active" : ""}
                  onClick={() => { setAuthTab("login"); setError(null); }}
                >
                  Connexion
                </button>
                <button
                  className={authTab === "register" ? "active" : ""}
                  onClick={() => { setAuthTab("register"); setError(null); }}
                >
                  Inscription
                </button>
              </div>

              {authTab === "login" ? (
                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Nom d'utilisateur</label>
                    <input
                      type="text"
                      className="form-control"
                      value={loginUsername}
                      onChange={(e) => setLoginUsername(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Mot de passe</label>
                    <input
                      type="password"
                      className="form-control"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Code manuel / Code enseignant
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: ".8rem" }}>(non requis pour les comptables et admins)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex : QL-PHY-TLE-1-RRB ou QL-ENS-..."
                      value={loginCodeManuel}
                      onChange={(e) => setLoginCodeManuel(formatCode(e.target.value))}
                    />
                    {!loginCodeManuel && (
                      <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginTop: 8, fontSize: ".8rem", color: "#78350f" }}>
                        <div style={{ fontWeight: 700, marginBottom: 5 }}><i className="bi bi-question-circle-fill me-1 text-warning"></i>Pourquoi ce code ?</div>
                        <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.9 }}>
                          <li><strong>Élève :</strong> Entrez le code imprimé dans votre <strong>manuel scolaire papier</strong> (ex : <span style={{ fontFamily: "monospace", fontWeight: 600 }}>QL-PHY-TLE-1-RRB</span>). Ce code prouve que vous avez accès au manuel.</li>
                          <li><strong>Enseignant :</strong> Entrez votre code personnel commençant par <span style={{ fontFamily: "monospace", fontWeight: 600 }}>QL-ENS-</span>, reçu lors de votre inscription.</li>
                          <li><strong>Admin / Comptable / Community Manager / Secrétariat / Brand Ambassador :</strong> Laissez ce champ <strong>vide</strong>, il ne vous concerne pas.</li>
                        </ul>
                      </div>
                    )}
                    {loginCodeManuel.length >= 3 && (() => {
                      const c = loginCodeManuel.toUpperCase();
                      if (estCodeAmbassadeur(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#fff6d6", color: "#071d50", borderColor: "#ffb900" }}>
                            <i className="bi bi-shop me-1"></i>
                            Brand Ambassador — laissez ce champ vide pour vous connecter
                          </div>
                        );
                      }
                      if (estCodeSecretariat(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#e0ecff", color: "#09245b", borderColor: "#2563eb" }}>
                            <i className="bi bi-person-workspace me-1"></i>
                            Secrétariat — laissez ce champ vide pour vous connecter
                          </div>
                        );
                      }
                      if (estCodeCommunity(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#ede9fe", color: "#5b21b6", borderColor: "#a78bfa" }}>
                            <i className="bi bi-megaphone-fill me-1"></i>
                            Community Manager — laissez ce champ vide pour vous connecter
                          </div>
                        );
                      }
                      if (c.includes("COMP")) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#fef3c7", color: "#92400e", borderColor: "#f59e0b" }}>
                            <i className="bi bi-calculator-fill me-1"></i>
                            Comptable — laissez ce champ vide pour vous connecter
                          </div>
                        );
                      }
                      if (c.includes("ENS")) {
                        return (
                          <div className="ql-code-detect mt-1">
                            <i className="bi bi-mortarboard-fill me-1"></i>
                            Enseignant détecté
                          </div>
                        );
                      }
                      const matiere = c.includes("PHY") ? "Physique" : c.includes("CHIM") ? "Chimie" : c.includes("MAT") ? "Mathématiques" : c.includes("INF") ? "Informatique" : null;
                      const niveau  = c.includes("TLE") ? "Terminale" : c.includes("PRE") ? "Première" : c.includes("SEC") ? "Seconde" : c.includes("TRO") ? "Troisième" : null;
                      if (!matiere && !niveau) return null;
                      return (
                        <div className="ql-code-detect mt-1">
                          <i className="bi bi-patch-check-fill me-1"></i>
                          {[niveau, matiere].filter(Boolean).join(" · ")} détecté(s)
                        </div>
                      );
                    })()}
                  </div>
                  {error && <div className="alert alert-danger py-2">{error}</div>}
                  <button type="submit" className="btn btn-orange w-100 py-2 mt-1">
                    Se connecter
                  </button>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <p className="mb-0 small">
                      Pas encore de compte ?{" "}
                      <button
                        type="button"
                        className="btn btn-link p-0 small"
                        onClick={() => { setAuthTab("register"); setError(null); }}
                      >
                        S'inscrire
                      </button>
                    </p>
                    <button
                      type="button"
                      className="btn btn-link p-0 small text-muted"
                      onClick={() => { setAuthTab("forgot"); setForgotEmail(""); setForgotSent(false); setForgotError(null); }}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
              ) : authTab === "forgot" ? (
                <div>
                  {forgotSent ? (
                    <div className="text-center py-3">
                      <i className="bi bi-envelope-check-fill text-success" style={{ fontSize: "2.5rem" }}></i>
                      <h6 className="mt-3 fw-bold">Email envoyé !</h6>
                      <p className="small text-muted">Si cet email est enregistré, vous recevrez un lien de réinitialisation dans quelques minutes. Vérifiez vos spams.</p>
                      <button className="btn btn-link small" onClick={() => { setAuthTab("login"); setForgotSent(false); }}>
                        Retour à la connexion
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgotPassword}>
                      <p className="small text-muted mb-3">Entrez votre adresse email. Vous recevrez un lien pour créer un nouveau mot de passe.</p>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Adresse email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          placeholder="votre@email.com"
                          required
                        />
                      </div>
                      {forgotError && <div className="alert alert-danger py-2">{forgotError}</div>}
                      <button type="submit" className="btn btn-orange w-100 py-2" disabled={forgotLoading}>
                        {forgotLoading ? "Envoi en cours..." : "Envoyer le lien"}
                      </button>
                      <p className="text-center mt-3 mb-0 small">
                        <button type="button" className="btn btn-link p-0 small" onClick={() => setAuthTab("login")}>
                          Retour à la connexion
                        </button>
                      </p>
                    </form>
                  )}
                </div>
              ) : authTab === "reset" ? (
                <div>
                  {resetDone ? (
                    <div className="text-center py-3">
                      <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "2.5rem" }}></i>
                      <h6 className="mt-3 fw-bold">Mot de passe modifié !</h6>
                      <p className="small text-muted">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
                      <button className="btn btn-orange mt-2" onClick={() => { setAuthTab("login"); setResetDone(false); }}>
                        Se connecter
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleResetPassword}>
                      <p className="small text-muted mb-3">Choisissez un nouveau mot de passe pour votre compte.</p>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Nouveau mot de passe</label>
                        <input
                          type="password"
                          className="form-control"
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          placeholder="Minimum 8 caractères"
                          required
                          minLength={8}
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Confirmer le mot de passe</label>
                        <input
                          type="password"
                          className="form-control"
                          value={resetPassword2}
                          onChange={(e) => setResetPassword2(e.target.value)}
                          placeholder="Répétez le mot de passe"
                          required
                        />
                      </div>
                      {resetError && <div className="alert alert-danger py-2">{resetError}</div>}
                      <button type="submit" className="btn btn-orange w-100 py-2" disabled={resetLoading}>
                        {resetLoading ? "Enregistrement..." : "Réinitialiser le mot de passe"}
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <form onSubmit={handleRegister}>
                  {/* Intro guide */}
                  <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "12px 14px", marginBottom: 18, fontSize: ".82rem", color: "#1e40af" }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}><i className="bi bi-info-circle-fill me-1"></i>Comment créer votre compte ?</div>
                    <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                      <li>Remplissez vos informations personnelles (nom, école, classe).</li>
                      <li>Entrez le <strong>code d'accès</strong> fourni par votre école ou qui se trouve à l'intérieur de la couverture de votre <strong>manuel papier</strong>.</li>
                      <li>Créez un identifiant et un mot de passe pour vous connecter.</li>
                    </ol>
                  </div>

                  <div className="mb-3 text-center">
                    <div className="ql-photo-preview-wrap">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Aperçu" className="ql-photo-preview" />
                      ) : (
                        <div className="ql-photo-placeholder">
                          <i className="bi bi-person-circle"></i>
                        </div>
                      )}
                    </div>
                    <label className="btn btn-outline-secondary btn-sm mt-2" style={{ cursor: "pointer" }}>
                      <i className="bi bi-camera me-1"></i>
                      {photoPreview ? "Changer la photo" : "Ajouter une photo (optionnel)"}
                      <input
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={handlePhotoChange}
                      />
                    </label>
                    <div className="form-text mt-1">Optionnel. Si vous ajoutez une photo, elle sera visible sur votre profil pour vous identifier plus facilement.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Nom complet</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex : Boris Nguema"
                      value={registerNomComplet}
                      onChange={(e) => setRegisterNomComplet(e.target.value)}
                      required
                    />
                    <div className="form-text">Entrez votre prénom suivi de votre nom de famille, exactement comme ils apparaissent dans votre carnet scolaire. Exemple : <em>Boris Nguema Mbarga</em>.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">École</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex : Lycée de Yaoundé"
                      value={registerEcole}
                      onChange={(e) => setRegisterEcole(e.target.value)}
                      required
                    />
                    <div className="form-text">Écrivez le nom complet de votre établissement scolaire. Exemple : <em>Lycée Bilingue de Bafoussam</em>.</div>
                  </div>
                  {/* Choix du rôle */}
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="estEnseignant"
                        checked={registerEstEnseignant}
                        onChange={(e) => { setRegisterEstEnseignant(e.target.checked); setRegisterCodeManuel(""); setRegisterClasseId(""); }}
                      />
                      <label className="form-check-label fw-semibold" htmlFor="estEnseignant">
                        <i className="bi bi-mortarboard-fill me-1 text-primary"></i>
                        Je suis enseignant (générateur d'épreuves)
                      </label>
                    </div>
                    <div className="form-text">
                      {registerEstEnseignant
                        ? "✅ Mode enseignant activé. Finalisez l'inscription — votre code d'accès personnel sera envoyé automatiquement à votre adresse email après validation."
                        : "Vous êtes élève ? Laissez cette case décochée. Cochez uniquement si vous êtes un professeur qui souhaite créer et générer des épreuves pour ses élèves."}
                    </div>
                    {registerEstEnseignant && (
                      <div className="alert alert-info py-2 mt-2 mb-0 small">
                        <i className="bi bi-info-circle-fill me-1"></i>
                        Un code d'accès unique sera généré automatiquement et envoyé à votre adresse email.
                      </div>
                    )}
                  </div>

                  {/* Classe — uniquement pour les élèves (pas enseignant, pas comptable) */}
                  {!registerEstEnseignant && !estCodePersonnel(registerCodeManuel) && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Classe</label>
                    <select
                      className="form-select"
                      value={registerClasseId}
                      onChange={(e) => setRegisterClasseId(e.target.value)}
                      required
                    >
                      <option value="">— Sélectionner une classe —</option>
                      {publicClasses.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <div className="form-text">Sélectionnez votre classe actuelle dans la liste. C'est important : cela détermine quels cours, exercices et QCM vous seront proposés sur la plateforme.</div>
                  </div>
                  )}

                  {/* Code manuel / Code enseignant — masqué si enseignant coché */}
                  {!registerEstEnseignant && <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="bi bi-key-fill me-1 text-warning"></i>
                      Code manuel / Code enseignant<span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control font-monospace"
                      placeholder="Ex : QL-PHY-TLE-1-RRB ou QL-ENS-... ou QL-COMP-... ou QL-COMM-... ou QL-SEC-... ou QL-AMB-..."
                      value={registerCodeManuel}
                      onChange={(e) => {
                        const val = formatCode(e.target.value);
                        setRegisterCodeManuel(val);
                        const up = val.toUpperCase();
                        if (up.includes("ENS") && !estCodePersonnel(up)) {
                          setRegisterEstEnseignant(true);
                        } else if (val.length > 0) {
                          setRegisterEstEnseignant(false);
                        }
                      }}
                      required
                    />
                    {!estCodePersonnel(registerCodeManuel) && !registerCodeManuel.toUpperCase().includes("ENS") && (
                      <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 12px", marginTop: 6, fontSize: ".8rem", color: "#78350f" }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}><i className="bi bi-question-circle-fill me-1 text-warning"></i>Où trouver votre code ?</div>
                        <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.8 }}>
                          <li><strong>Élève :</strong> Le code est imprimé à l'intérieur de la couverture de votre <strong>manuel scolaire papier</strong> (exemple : <span style={{ fontFamily: "monospace" }}>QL-PHY-TLE-1-RRB</span>). Si vous ne l'avez pas, demandez à votre enseignant.</li>
                          <li><strong>Enseignant :</strong> Votre code commence par <span style={{ fontFamily: "monospace" }}>QL-ENS-</span>. Il vous a été communiqué par l'administration de la plateforme.</li>
                        </ul>
                      </div>
                    )}
                    {registerCodeManuel.length >= 3 && (() => {
                      const c = registerCodeManuel.toUpperCase();
                      if (estCodeAmbassadeur(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#fff6d6", color: "#071d50", borderColor: "#ffb900" }}>
                            <i className="bi bi-shop me-1"></i>
                            Brand Ambassador détecté — accès à l'espace des ventes de manuels
                          </div>
                        );
                      }
                      if (estCodeSecretariat(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#e0ecff", color: "#09245b", borderColor: "#2563eb" }}>
                            <i className="bi bi-person-workspace me-1"></i>
                            Secrétariat détecté — accès à l'espace secrétariat
                          </div>
                        );
                      }
                      if (estCodeCommunity(c)) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#ede9fe", color: "#5b21b6", borderColor: "#a78bfa" }}>
                            <i className="bi bi-megaphone-fill me-1"></i>
                            Community Manager détectée — accès à l'espace communication
                          </div>
                        );
                      }
                      if (c.includes("COMP")) {
                        return (
                          <div className="ql-code-detect mt-1" style={{ background: "#fef3c7", color: "#92400e", borderColor: "#f59e0b" }}>
                            <i className="bi bi-calculator-fill me-1"></i>
                            Comptable détecté — accès comptabilité activé
                          </div>
                        );
                      }
                      if (c.includes("ENS")) {
                        return (
                          <div className="ql-code-detect mt-1">
                            <i className="bi bi-mortarboard-fill me-1"></i>
                            Enseignant détecté
                          </div>
                        );
                      }
                      const matiere = c.includes("PHY") ? "Physique" : c.includes("CHIM") ? "Chimie" : c.includes("MAT") ? "Mathématiques" : c.includes("INF") ? "Informatique" : null;
                      const niveau  = c.includes("TLE") ? "Terminale" : c.includes("PRE") ? "Première" : c.includes("SEC") ? "Seconde" : c.includes("TRO") ? "Troisième" : null;
                      if (!matiere && !niveau) return null;
                      return (
                        <div className="ql-code-detect mt-1">
                          <i className="bi bi-patch-check-fill me-1"></i>
                          {[niveau, matiere].filter(Boolean).join(" · ")} détecté(s)
                        </div>
                      );
                    })()}
                  </div>}
                  {!registerEstEnseignant && <div className="mb-3">
                    <label className="form-label fw-semibold">Code de parrainage <span className="text-muted">(facultatif)</span></label>
                    <input type="text" className="form-control font-monospace" placeholder="Ex : QLP-12AB34CD56"
                      value={registerReferralCode} onChange={(e) => setRegisterReferralCode(e.target.value.toUpperCase().trim())} />
                    <div className="form-text">Ce code personnel est distinct du code de votre manuel.</div>
                  </div>}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Nom d'utilisateur</label>
                    <div className="form-text mb-1">C'est l'identifiant que vous utiliserez pour vous <strong>connecter</strong> chaque jour. Choisissez quelque chose de simple et facile à retenir, sans espaces ni accents. Exemple : <em>BorisNguema</em> ou <em>boris2024</em>.</div>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control${usernameStatus === "available" ? " is-valid" : usernameStatus === "taken" ? " is-invalid" : ""}`}
                        value={registerUsername}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        required
                        placeholder="Choisissez un identifiant unique"
                      />
                      {usernameStatus === "checking" && (
                        <span className="input-group-text">
                          <span className="spinner-border spinner-border-sm text-secondary" />
                        </span>
                      )}
                      {usernameStatus === "available" && (
                        <span className="input-group-text text-success"><i className="bi bi-check-circle-fill"></i></span>
                      )}
                      {usernameStatus === "taken" && (
                        <span className="input-group-text text-danger"><i className="bi bi-x-circle-fill"></i></span>
                      )}
                    </div>
                    {usernameStatus === "available" && (
                      <div style={{ fontSize:"0.75rem", color:"#22c55e", marginTop:3 }}>✓ Nom d'utilisateur disponible</div>
                    )}
                    {usernameStatus === "taken" && (
                      <div>
                        <div style={{ fontSize:"0.75rem", color:"#ef4444", marginTop:3 }}>
                          ✗ Ce nom d'utilisateur est déjà pris.
                        </div>
                        {usernameSuggestions.length > 0 && (
                          <div style={{ marginTop:6, padding:"8px 10px", borderRadius:8, background:"#fff7ed", border:"1px solid #fed7aa" }}>
                            <div style={{ fontSize:"0.72rem", color:"#c2410c", fontWeight:600, marginBottom:5 }}>
                              <i className="bi bi-lightbulb-fill me-1" style={{ color:"#f59e0b" }}></i>
                              Suggestions disponibles :
                            </div>
                            {usernameSuggestions.map((s, i) => (
                              <div
                                key={i}
                                onClick={() => { setRegisterUsername(s); setUsernameStatus("available"); setUsernameSuggestions([]); }}
                                style={{ cursor:"pointer", padding:"3px 8px", marginBottom:3, borderRadius:5, background:"#fff", border:"1px solid #fdba74", fontFamily:"monospace", fontSize:"0.82rem", fontWeight:600, color:"#9a3412" }}
                                onMouseEnter={e => e.currentTarget.style.background="#fff7ed"}
                                onMouseLeave={e => e.currentTarget.style.background="#fff"}
                              >
                                👤 {s}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Email</label>
                    <div className="form-text mb-1">Entrez une adresse email valide que vous consultez régulièrement. Elle servira à récupérer votre mot de passe en cas d'oubli.</div>
                    <input
                      type="email"
                      className="form-control"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Mot de passe</label>
                    <div className="form-text mb-1">Choisissez un mot de passe <strong>secret</strong> que vous seul connaissez. Il doit contenir au moins 8 caractères. Notez-le quelque part pour ne pas l'oublier !</div>
                    {/* Champ mot de passe avec œil */}
                    <div className="input-group">
                      <input
                        type={showRegisterPassword ? "text" : "password"}
                        className="form-control"
                        value={registerPassword}
                        onChange={(e) => { setRegisterPassword(e.target.value); setShowPasswordSuggestions(false); }}
                        required
                        placeholder="Minimum 8 caractères"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowRegisterPassword(v => !v)}
                        tabIndex={-1}
                        title={showRegisterPassword ? "Masquer" : "Afficher"}
                      >
                        <i className={`bi ${showRegisterPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                      </button>
                    </div>

                    {/* Barre de force */}
                    {registerPassword.length > 0 && (() => {
                      const { score, label, color, criteria } = getPasswordStrength(registerPassword);
                      return (
                        <div className="mt-2">
                          {/* Barre */}
                          <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                            {[1,2,3,4].map(i => (
                              <div key={i} style={{
                                flex: 1, height: 5, borderRadius: 4,
                                background: i <= score ? color : "#e2e8f0",
                                transition: "background 0.3s",
                              }} />
                            ))}
                          </div>
                          <div style={{ fontSize: "0.78rem", color, fontWeight: 600, marginBottom: 4 }}>
                            {label}
                          </div>
                          {/* Critères */}
                          <ul style={{ fontSize: "0.75rem", color: "#64748b", margin: 0, paddingLeft: 16 }}>
                            {[
                              { key: "length",    text: "Au moins 8 caractères" },
                              { key: "uppercase", text: "Une lettre majuscule" },
                              { key: "number",    text: "Un chiffre" },
                              { key: "special",   text: "Un caractère spécial (@, #, !, …)" },
                            ].map(c => (
                              <li key={c.key} style={{ color: criteria[c.key] ? "#22c55e" : "#94a3b8" }}>
                                {criteria[c.key] ? "✓" : "○"} {c.text}
                              </li>
                            ))}
                          </ul>

                          {/* Bouton suggestions si mot de passe faible/moyen */}
                          {score < 4 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary mt-2 w-100"
                              style={{ fontSize: "0.78rem" }}
                              onClick={() => setShowPasswordSuggestions(v => !v)}
                            >
                              <i className="bi bi-magic me-1"></i>
                              {showPasswordSuggestions ? "Masquer les suggestions" : "Suggérer des mots de passe mémorables"}
                            </button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Panneau de suggestions */}
                    {showPasswordSuggestions && (() => {
                      const suggestions = generatePasswordSuggestions(registerNomComplet);
                      return (
                        <div style={{
                          marginTop: 8, padding: "10px 12px", borderRadius: 8,
                          background: "#f0f9ff", border: "1px solid #bae6fd",
                        }}>
                          <div style={{ fontSize: "0.75rem", color: "#0369a1", fontWeight: 600, marginBottom: 6 }}>
                            <i className="bi bi-lightbulb-fill me-1 text-warning"></i>
                            Cliquez sur un mot de passe pour l'utiliser :
                          </div>
                          {suggestions.map((s, i) => (
                            <div
                              key={i}
                              onClick={() => { setRegisterPassword(s); setShowPasswordSuggestions(false); setShowRegisterPassword(true); }}
                              style={{
                                cursor: "pointer", padding: "5px 10px", marginBottom: 4,
                                borderRadius: 6, background: "#fff", border: "1px solid #e0f2fe",
                                fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 600,
                                color: "#0c4a6e", letterSpacing: "0.05em",
                                transition: "background 0.15s",
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = "#e0f2fe"}
                              onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                              title="Cliquer pour utiliser ce mot de passe"
                            >
                              🔑 {s}
                            </div>
                          ))}
                          <div style={{ fontSize: "0.7rem", color: "#64748b", marginTop: 6 }}>
                            <i className="bi bi-info-circle me-1"></i>
                            Notez bien votre mot de passe avant de valider.
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {error && <div className="alert alert-danger py-2">{error}</div>}
                  {registrationStage && <div className="ql-registration-progress" role="status" aria-live="polite"><span className="spinner-border spinner-border-sm" aria-hidden="true" /><span>{registrationStage}</span></div>}
                  <button type="submit" className="btn btn-orange w-100 py-2 mt-1" disabled={!!registrationStage} aria-busy={!!registrationStage}>
                    {registrationStage ? "Veuillez patienter…" : "S’inscrire"}
                  </button>
                  <p className="text-center mt-3 mb-0 small">
                    Déjà un compte ?{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 small"
                      onClick={() => { setAuthTab("login"); setError(null); }}
                    >
                      Se connecter
                    </button>
                  </p>
                </form>
              )}
            </div>
          </div>
        )}

        <AIAssistantComingSoon />
        <BackToTopButton />
      </div>
    );
  }

  // Les anciens comptes générateurs peuvent être marqués `is_generateur`
  // sans avoir encore de `code_enseignant`. Sous Windows, ils appartiennent
  // néanmoins à l'espace enseignant et ne doivent jamais tomber sur le mur
  // d'abonnement réservé aux élèves.
  if (
    (isWindowsApp || isAndroidDirect) &&
    (user.is_enseignant || user.is_generateur || localStorage.getItem(WINDOWS_TEACHER_SESSION_KEY) === "1")
  ) {
    return (
      <><TeacherApp onLogout={handleLogout} /><AIAssistantComingSoon /></>
    );
  }

  /* ── ADMIN PANEL (staff / superadmin / comptable) ── */
  if (user.is_staff || user.is_superuser || user.is_comptable || user.is_community_manager || user.is_secretaire || user.is_brand_ambassador) {
    return (
      <>
        <AdminPanel user={user} onLogout={handleLogout} onUserUpdate={setUser} />
        <AIAssistantComingSoon />
      </>
    );
  }

  /* ── MUR D'ABONNEMENT (élève sans abonnement actif) ── */
  if (!user.subscription_active) {
    if (isAndroidPlayConsumptionOnly) {
      return <><AndroidSubscriptionStatus user={user} onLogout={handleLogout} onRefresh={async () => {
        await loadProfile({ openTeacherSpace: false });
        await loadDashboard();
        await loadModules();
      }} /><AIAssistantComingSoon /></>;
    }
    return (
      <>
        <SubscriptionWall
          user={user}
          onLogout={handleLogout}
          onSubscribed={async () => {
            await loadProfile();
            await loadDashboard();
            await loadModules();
          }}
        />
        <AIAssistantComingSoon />
      </>
    );
  }

  /* ── TABLEAU DE BORD (connecté + abonnement actif) ── */
  return (
    <>
      <WelcomeBonusModal bonus={welcomeBonus} onClose={() => {
        setWelcomeBonus(null);
        setShowTour(true);
      }} />
      {showTour && <HelpTour onClose={closeTour} />}
      <StudentDashboard
        user={user}
        summary={dashboardSummary}
        modules={modules}
        onLogout={handleLogout}
        onUserUpdate={setUser}
        onShowTour={openTour}
        consumptionOnly={isAndroidPlayConsumptionOnly}
        onRefreshAccount={async () => {
          await loadProfile({ openTeacherSpace: false });
          await loadDashboard();
          await loadModules();
        }}
      />
      <AIAssistantComingSoon />
    </>
  );
}

export default App;
